/*!

=========================================================
* Argon Dashboard React - v1.2.3
=========================================================

* Product Page: https://www.creative-tim.com/product/argon-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)
* Licensed under MIT (https://github.com/creativetimofficial/argon-dashboard-react/blob/master/LICENSE.md)

* Coded by Creative Tim

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

*/
// reactstrap components
import { Card, CardHeader, Table, Container, Row } from 'reactstrap';
import { GlobalContext } from '../api/GlobalContext';
import { Fragment, useContext, useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import HashState from '../variables/hash';
import Header from 'components/Headers/Header';
import LearnEditsPanel from '../components/Analytics/LearnEditsPanel';
import DevMasterDashboard from './DevMasterDashboard';
import NoteQualityDashboard from './NoteQualityDashboard';
import FeedbackDashboard from './FeedbackDashboard';
import InstructionAssistantPrompts from './InstructionAssistantPrompts';
import FeatureRollouts from './FeatureRollouts';
import InstructionPush from './InstructionPush';
import EMRInjectorDebug from './EMRInjectorDebug';
import EMRDirectives from './settingsViews/EMRDirectives';
import MockEMRDevTools from './MockEMRDevTools';
import { getEmrNoteTypesLabel } from '../components/Misc/utils';
import { FeatureInfo } from '../components/Misc/FeatureInfo';

// Additional imports
import { Form, FormGroup, Label, Input, Col, Button } from 'reactstrap';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { Line } from 'react-chartjs-2';

// Exported for reuse (e.g., DevMasterDashboard)
export const FRONTEND_FEATURES = {
  agentic_chat: [true, false],
  synapse: [true, false],
  show_multi_recorder: [true, false],
  go_to_emr: [true, false],
  sync_cal: [true, false],
  get_all_notes: [true, false],
  raintree_note_capture: [true, false],
  user_note_rules: [true, false],
  new_rt_integration: [true, false],
  force_onboarding_checklist: [true, false],
  // flex++ ("full Prompt"): routes Prompt EMR through NewPromptInject, which
  // asks the whole note as one nested flex++ question instead of ~40 flat ones.
  new_prompt_integration: [true, false],
  cora_rom: [true, false],
  new_webpt_soap2_integration: [true, false],
  new_webpt_integration: [true, false],
  webpt2_chrome_freeze_fix: [true, false],
  pte_custom_note_types: [true, false],
  comprehend_live: [true, false],
  multirecord_tray: [true, false],
  multirecord_context_and_error_prevention: [true, false],
  emr_beta: [true, false],
  compliance_sign_off_gate: [true, false],
  learn_plus: [true, false],
  // Scorecard: relabels the compliance surface as "Scorecard" and swaps the
  // backend grading directive to conversion coaching. Listed here (not only in
  // the backend FEATURES map) so it shows in feature management without waiting
  // on a Lambda deploy.
  scorecard: [true, false],
  // Record this user's sessions even on a machine the server classed too slow
  // to record, past that class's boot limit, and on an EMR that is off by
  // default (Prompt). For when someone we cannot otherwise see is reporting a
  // bug. Still subject to the recorder's own buffer and chunk limits.
  replay_force: [true, false],
  // Continuous session replay for this user. Unset means ON; set false to stop
  // it. replay_force and bug-report recordings are not affected.
  session_replay: [true, false],
  dictation: [true, false],
  patient_match_warning: [true, false],
  // Vendor Apps: the Apps tab + iframe host and the Admin Settings → Apps
  // registration tab (docs/vendor-apps-contract.md).
  vendor_apps: [true, false],
  comprehend_feedback: [true, false],
  // Support Chat opens Zendesk instead of Crisp.
  zendesk_support_chat: [true, false],
  visits_schedule: [true, false],
  // Shows the "Help train Hey Comprehend" invite (views/voiceTraining) in the
  // web app. Off by default; the page itself is public at /User/audio/collection.
  wake_word_collection: [true, false],
};

const GroupSubscription = ({ apiClient }) => {
  const [rsp, setRsp] = useState({ Status: 'Response will show here.' });
  const [emails, setEmails] = useState('');
  const [subscriptionEmail, setSubscriptionEmail] = useState('');
  const [selectedDate, setSelectedDate] = useState(null);
  const [seats, setSeats] = useState('');
  const [groupNames, setGroupNames] = useState('');

  // Handler for setting expiration
  const searchSubscription = () => {
    setRsp({
      Status: 'Searching group subscription based on subscription email...',
    });
    apiClient
      .adminSearchGroupSubscription(subscriptionEmail)
      .then((response) => {
        setRsp(response); // Update rsp state with the response
      })
      .catch((error) => {
        console.error('Error setting expiration:', error);
      });
  };

  const createSubsciption = () => {
    setRsp({ Status: 'Creating a new subscription...' });
    apiClient
      .adminCreateGroupSubscription(
        subscriptionEmail,
        seats,
        selectedDate instanceof Date
          ? selectedDate.toISOString().slice(0, -1)
          : null,
        emails,
        groupNames,
      )
      .then((response) => {
        setRsp(response); // Update rsp state with the response
      })
      .catch((error) => {
        console.error('Error setting expiration:', error);
      });
  };

  // Handler for updating a subscription
  const updateSubsciption = () => {
    setRsp({ Status: 'Assigning users or seats to subscription...' });
    apiClient
      .adminUpateGroupSubscription(
        subscriptionEmail,
        seats,
        selectedDate instanceof Date
          ? selectedDate.toISOString().slice(0, -1)
          : null,
        emails,
        groupNames,
      )
      .then((response) => {
        setRsp(response); // Update rsp state with the response
      })
      .catch((error) => {
        console.error('Error creating promo:', error);
      });
  };

  // Handler for making a user an admin
  const makeUserAdmin = () => {
    setRsp({ Status: 'Making a user as Admin for assocaited subscripton' });
    apiClient
      .adminMakeUserGroupAdmin(subscriptionEmail)
      .then((response) => {
        setRsp(response); // Update rsp state with the response
      })
      .catch((error) => {
        console.error('Error creating promo:', error);
      });
  };
  return (
    <>
      <p>
        Search, Create or Update Group subscription based on the subscription
        email.
      </p>
      <Form>
        <FormGroup row>
          <Label for="subscriptionEmailInput" sm={2}>
            Subscription/Admin Email
          </Label>
          <Col sm={10}>
            <Input
              type="email"
              name="subscriptionEmail"
              id="subscriptionEmailInput"
              placeholder="Enter email associated with subsciption"
              value={subscriptionEmail}
              onChange={(e) => setSubscriptionEmail(e.target.value)}
            />
          </Col>
        </FormGroup>
        <FormGroup row>
          <Label for="seatsInput" sm={2}>
            Seats
          </Label>
          <Col sm={10}>
            <Input
              type="number"
              name="seats"
              id="seatsInput"
              placeholder="Enter number of seats to assign to subscription"
              value={seats}
              onChange={(e) => setSeats(e.target.value)}
            />
          </Col>
        </FormGroup>
        <FormGroup row>
          <Label for="groupNamesInput" sm={2}>
            Group Names
          </Label>
          <Col sm={10}>
            <Input
              type="text"
              name="groupNames"
              id="groupNamesInput"
              placeholder="Enter group names, comma separated"
              value={groupNames}
              onChange={(e) => setGroupNames(e.target.value)}
            />
          </Col>
        </FormGroup>
        <FormGroup row>
          <Label for="emailsInput" sm={2}>
            User Emails
          </Label>
          <Col sm={10}>
            <Input
              type="text"
              name="emails"
              id="emailsInput"
              placeholder="Enter emails to assign to subscription, comma separated"
              value={emails}
              onChange={(e) => setEmails(e.target.value)}
            />
          </Col>
        </FormGroup>
        <FormGroup row>
          <Label for="datePicker" sm={2}>
            Expiration Date
          </Label>
          <Col sm={10}>
            <DatePicker
              selected={selectedDate}
              onChange={(date) => setSelectedDate(date)}
            />
          </Col>
        </FormGroup>

        <FormGroup row>
          <Col sm={{ size: 10, offset: 2 }}>
            <Button color="primary" onClick={searchSubscription}>
              Search Subscription
            </Button>
            <Button color="primary" onClick={createSubsciption}>
              Create Subscription
            </Button>
            <Button color="primary" onClick={updateSubsciption}>
              Assign to Subscription
            </Button>
            <Button color="primary" onClick={makeUserAdmin}>
              Make User Admin
            </Button>
          </Col>
        </FormGroup>
      </Form>
      <pre>{JSON.stringify(rsp, null, 2)}</pre>
    </>
  );
};

const SetExpirationAndPromo = ({ apiClient }) => {
  const [rsp, setRsp] = useState({ Status: 'Response will show here.' });
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [days, setDays] = useState('');

  // Handler for setting expiration
  const handleSetExpiration = () => {
    setRsp({ Status: 'Setting expiration...' });
    // Assuming selectedDate is in a format suitable for your API
    apiClient
      .adminSetExpiration(
        email,
        code,
        selectedDate.toISOString().slice(0, -1),
        true,
      )
      .then((response) => {
        setRsp(response); // Update rsp state with the response
      })
      .catch((error) => {
        console.error('Error setting expiration:', error);
        // Handle error (e.g., updating rsp with error message or a default message)
      });
  };

  const handleExtendFreeTrial = () => {
    setRsp({ Status: 'Extending Trial...' });
    // Assuming selectedDate is in a format suitable for your API
    apiClient
      .adminSetExpiration(email, code, selectedDate.toISOString().slice(0, -1))
      .then((response) => {
        setRsp(response); // Update rsp state with the response
      })
      .catch((error) => {
        console.error('Error setting expiration:', error);
        // Handle error (e.g., updating rsp with error message or a default message)
      });
  };

  // Handler for creating a promo
  const handleCreatePromo = () => {
    setRsp({ Status: 'Creating promo...' });
    // Again, assuming selectedDate is correctly formatted
    apiClient
      .adminCreatePromo(code, selectedDate.toISOString().slice(0, -1), days)
      .then((response) => {
        setRsp(response); // Update rsp state with the response
      })
      .catch((error) => {
        console.error('Error creating promo:', error);
        // Handle error
      });
  };

  return (
    <>
      <p>
        You can set the expiration for an account (new or existing) here. You
        can also make promo codes that will give any user that signs up with it,
        an expiration on the set date.
      </p>
      <Form>
        <FormGroup row>
          <Label for="emailInput" sm={2}>
            Email
          </Label>
          <Col sm={10}>
            <Input
              type="email"
              name="email"
              id="emailInput"
              placeholder="Enter email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </Col>
        </FormGroup>
        <FormGroup row>
          <Label for="codeInput" sm={2}>
            "Referral Code" (Tracking)
          </Label>
          <Col sm={10}>
            <Input
              type="text"
              name="code"
              id="codeInput"
              placeholder="Enter code"
              value={code}
              onChange={(e) => setCode(e.target.value)}
            />
          </Col>
        </FormGroup>
        <FormGroup row>
          <Label for="datePicker" sm={2}>
            Date
          </Label>
          <Col sm={10}>
            <DatePicker
              selected={selectedDate}
              onChange={(date) => setSelectedDate(date)}
            />
          </Col>
        </FormGroup>
        {/* <FormGroup row>
            <Label for="daysInput" sm={2}>Days</Label>
            <Col sm={10}>
                <Input type="number" name="days" id="daysInput" placeholder="Enter number of days" value={days} onChange={(e) => setDays(e.target.value)} />
            </Col>
        </FormGroup> */}
        <FormGroup row>
          <Col sm={{ size: 10, offset: 2 }}>
            <Button color="primary" onClick={handleSetExpiration}>
              Set Expiration
            </Button>
            <Button color="primary" onClick={handleExtendFreeTrial}>
              Extend Free Trial
            </Button>
            <Button color="secondary" onClick={handleCreatePromo}>
              Create Promo
            </Button>
          </Col>
        </FormGroup>
      </Form>
      <pre>{JSON.stringify(rsp, null, 2)}</pre>
    </>
  );
};

// Create a new component that will be meant for the search user mode, it should have an email and a button to search for the user then a table to display the user's information
const SearchUser = ({ apiClient }) => {
  const [searchKey, setSearchKey] = useState('');
  const [rsp, setRsp] = useState({ Status: 'Response will show here.' });
  const [users, setUsers] = useState([]);

  const handleSearch = () => {
    setRsp({ Status: 'Searching user...' });
    apiClient
      .adminSearchUser(searchKey)
      .then((response) => {
        setRsp(response); // Update rsp state with the response
        // Check if the response is an array of users
        if (Array.isArray(response)) {
          setUsers(response);
        }
      })
      .catch((error) => {
        console.error('Error searching user:', error);

        setRsp({ Status: `Error searching user. - ${error}` });
        // Handle error (e.g., updating rsp with error message or a default message)
      });
  };

  return (
    <>
      <p>
        You can Search for a specific user here. Currently we only look at their
        Name field and it's case sensitive
      </p>
      <Form
        onSubmit={(e) => {
          e.preventDefault();
          handleSearch();
        }}
      >
        <FormGroup row>
          <Label for="searchKey" sm={2}>
            Search Key
          </Label>
          <Col sm={10}>
            <Input
              name="searchKey"
              id="searchKey"
              placeholder="Search Key (Case Sensitive)"
              value={searchKey}
              onChange={(e) => setSearchKey(e.target.value)}
            />
          </Col>
        </FormGroup>
        <FormGroup row>
          <Col sm={{ size: 10, offset: 2 }}>
            <Button color="primary" type="submit">
              Search User
            </Button>
          </Col>
        </FormGroup>
      </Form>
      <Table>
        <thead>
          <tr>
            <th>Name</th>
            <th>Email</th>
          </tr>
        </thead>
        <tbody>
          {users.map((user, index) => (
            <tr key={index}>
              <td>{user.name}</td>
              <td>{user.email}</td>
            </tr>
          ))}
        </tbody>
      </Table>
      <pre>{JSON.stringify(rsp, null, 2)}</pre>
    </>
  );
};

// Create a new component that will be meant for the get configs mode, it should have a button to get the configs and a table to display the configs
const GetConfigs = ({ apiClient }) => {
  const [email, setEmail] = useState('');
  const [rsp, setRsp] = useState({ Status: 'Response will show here.' });
  const [configs, setConfigs] = useState([]);

  const handleLookup = () => {
    setRsp({ Status: "Getting User's Configs..." });
    apiClient
      .adminGetUserConfigs(email)
      .then((response) => {
        setRsp(response); // Update rsp state with the response
        // Check if the response is an array of users
        if (Array.isArray(response)) {
          setConfigs(response);
        }
      })
      .catch((error) => {
        console.error('Error searching user:', error);
        setRsp({ Status: `Error searching user. - ${error}` });
        // Handle error (e.g., updating rsp with error message or a default message)
      });
  };

  return (
    <>
      <p>
        You can get a specific user's configs, useful for manipulating them on
        your end...
      </p>
      <Form
        onSubmit={(e) => {
          e.preventDefault();
          handleLookup();
        }}
      >
        <FormGroup row>
          <Label for="email" sm={2}>
            Email
          </Label>
          <Col sm={10}>
            <Input
              name="email"
              id="email"
              placeholder="User Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </Col>
        </FormGroup>
        <FormGroup row>
          <Col sm={{ size: 10, offset: 2 }}>
            <Button color="primary" type="submit">
              Get Configs
            </Button>
          </Col>
        </FormGroup>
      </Form>
      <Table>
        <thead>
          <tr>
            <th>Name</th>
            <th>Id</th>
            <th>EMR URL</th>
            <th>EMR Note Type</th>
            <th>Last Modified</th>
            <th>Created At</th>
            <th>Second Pass</th>
          </tr>
        </thead>
        <tbody>
          {configs.map((user, index) => (
            <tr key={index}>
              <td>{user.name}</td>
              <td>{user.id}</td>
              <td>{user.emr_url}</td>
              <td>{getEmrNoteTypesLabel(user, 'No EMR note types')}</td>
              <td>{user.updated_at}</td>
              <td>{user.created_at}</td>
              <td>{user.second_pass}</td>
            </tr>
          ))}
        </tbody>
      </Table>
      <pre>{JSON.stringify(rsp, null, 2)}</pre>
    </>
  );
};

// New Component for getting user details
const GetUserDetails = ({ apiClient }) => {
  const [email, setEmail] = useState('');
  const [rsp, setRsp] = useState({ Status: 'Response will show here.' });

  const handleLookup = () => {
    setRsp({ Status: "Getting User's Details..." });
    apiClient
      .adminGetUserDetails(email)
      .then((response) => {
        setRsp(response); // Update rsp state with the response
      })
      .catch((error) => {
        console.error('Error searching user:', error);
        setRsp({ Status: `Error searching user. - ${error}` });
        // Handle error (e.g., updating rsp with error message or a default message)
      });
  };

  async function setModel(model) {
    // Model should be pro or normal
    setRsp({ Status: `Setting ${model}...` });
    try {
      const rsp = await apiClient.adminSetUserDetails(
        email,
        'default_model',
        model,
      );
      setRsp(rsp);
    } catch (error) {
      console.error(`Error setting ${model}:`, error);
      setRsp({ Status: `Error setting ${model}. - ${error}` });
    }
  }

  return (
    <>
      <p>
        You can get a specific user's details, useful for knowing how many times
        they have used the platform
      </p>
      <Form
        onSubmit={(e) => {
          e.preventDefault();
          handleLookup();
        }}
      >
        <FormGroup row>
          <Label for="email" sm={2}>
            Email
          </Label>
          <Col sm={10}>
            <Input
              name="email"
              id="email"
              placeholder="User Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </Col>
        </FormGroup>
        <FormGroup row>
          <Col sm={{ size: 10, offset: 2 }}>
            <Button color="primary" type="submit">
              Get Details
            </Button>
          </Col>
        </FormGroup>
        <FormGroup row>
          <Col sm={{ size: 10, offset: 2 }}>
            <Button color="secondary" onClick={() => setModel('normal')}>
              GPT3.5
            </Button>
            <Button color="primary" onClick={() => setModel('pro')}>
              GPT4
            </Button>
          </Col>
        </FormGroup>
      </Form>
      <pre>{JSON.stringify(rsp, null, 2)}</pre>
    </>
  );
};

// New Component for getting user details
const GetUsage = ({ apiClient }) => {
  const navigate = useNavigate();
  const [emails, setEmails] = useState(HashState());
  // Default to 30 days before
  const now = new Date();
  now.setDate(now.getDate() + 1);
  const [t1, setT1] = useState(now);
  const before = new Date();
  before.setDate(before.getDate() - 30);
  const [t2, setT2] = useState(before);
  const [getGroupUsers, setGetGroupUsers] = useState(true);

  const [rsp, setRsp] = useState({ Status: 'Response will show here.' });

  const TOTAL_MULTIPLIER = 1000000;

  const handleLookup = () => {
    setRsp({ Status: "Getting User's Details..." });
    navigate(`/User/admin/dashboard#${emails}`);
    let users = emails.split(',').map((emails) => emails.trim());
    if (users.length === 0 || emails === '') {
      users = null;
    }
    apiClient
      .adminDetailUsage(
        t1.toISOString(),
        t2.toISOString(),
        users,
        getGroupUsers,
      )
      .then((response) => {
        setRsp(response); // Update rsp state with the response
      })
      .catch((error) => {
        console.error('Error searching user:', error);
        setRsp({ Status: `Error searching user. - ${error}` });
        // Handle error (e.g., updating rsp with error message or a default message)
      });
  };

  return (
    <>
      <p>
        You can get a specific user's details, useful for knowing how many times
        they have used the platform.
      </p>
      <p>
        When no emails are listed, we get all users. You can get a specific user
        or a list of users using a comma separated list of emails. They will be
        ranked in a table as well as totals calculated. If you'd like to save
        specific user subsets you can bookmark the link and when you go to said
        link, your list of emails will be saved.
      </p>
      <Form
        onSubmit={(e) => {
          e.preventDefault();
          handleLookup();
        }}
      >
        <FormGroup row>
          <Label for="email" sm={2}>
            Emails
          </Label>
          <Col sm={10}>
            <Input
              name="email"
              id="email"
              placeholder="User Email"
              value={emails}
              onChange={(e) => setEmails(e.target.value)}
            />
          </Col>

          <Col sm={{ size: 10, offset: 2 }}>
            <FormGroup
              check
              style={{ marginTop: '0.5rem', marginBottom: '0.5rem' }}
            >
              <Label check htmlFor="getGroupUsers">
                <Input
                  type="checkbox"
                  id="getGroupUsers"
                  checked={getGroupUsers}
                  onChange={(e) => setGetGroupUsers(e.target.checked)}
                />{' '}
                Get entire group?
              </Label>
            </FormGroup>
          </Col>

          <Label for="datePicker" sm={2}>
            Start Date
          </Label>
          <Col sm={10}>
            <DatePicker
              selected={t1}
              onChange={(date) => setT1(date)}
              showTimeSelect
              timeFormat="HH:mm"
              timeIntervals={15}
              timeCaption="time"
              dateFormat="MMMM d, yyyy h:mm aa"
            />
          </Col>

          <Label for="datePicker" sm={2}>
            End Date
          </Label>
          <Col sm={10}>
            <DatePicker
              selected={t2}
              onChange={(date) => setT2(date)}
              showTimeSelect
              timeFormat="HH:mm"
              timeIntervals={15}
              timeCaption="time"
              dateFormat="MMMM d, yyyy h:mm aa"
            />
          </Col>
          <Col sm={10}>
            <Button color="primary" type="submit">
              Submit
            </Button>
          </Col>
        </FormGroup>
        {rsp.users && (
          <div>
            <h1>Total</h1>
            <FormGroup row>
              <Col sm={10}>
                <p>Number of Users: {Object.keys(rsp.users).length}</p>
                <p>Total Visits: {rsp.totalVisits.toLocaleString()}</p>
                <p>
                  Total Hours Transcribed:{' '}
                  {rsp.totalHoursTranscribed.toLocaleString()}
                </p>
                <p>
                  Total Input Tokens Used:{' '}
                  {rsp.totalInputTokensUsed.toLocaleString()}
                </p>
                <p>
                  Total Output Tokens Used:{' '}
                  {rsp.totalOutputTokensUsed.toLocaleString()}
                </p>
                <p>
                  Total Cost:{' '}
                  {(rsp.totalCost / TOTAL_MULTIPLIER).toLocaleString()}
                </p>
              </Col>
            </FormGroup>
          </div>
        )}
      </Form>
      {rsp.users && (
        <>
          <h1>Leaderboard</h1>
          <Table>
            <thead>
              <tr>
                <th>Rank</th>
                <th>Email</th>
                <th>Cost</th>
                <th># of Visits</th>
                <th># of Configs</th>
                <th># of Shared Configs</th>
                <th># of Active Builders</th>
                <th># of Disabled Builders</th>
                <th>Hours</th>
                <th>Expiration</th>
                <th>Last Used</th>
                <th>Input Tokens</th>
                <th>Output Tokens</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(rsp.users).map(([email, details], index) => (
                <tr key={email}>
                  <td>{index + 1}</td>
                  <td>{email}</td>
                  <td>{(details.cost / TOTAL_MULTIPLIER).toLocaleString()}</td>
                  <td>{details.visits.length.toLocaleString()}</td>
                  <td>{details.configs}</td>
                  <td>{details.shared_configs}</td>
                  <td>{details.active_builder_configs}</td>
                  <td>{details.disabled_builder_configs}</td>
                  <td>{(details.seconds / 60 / 60).toLocaleString()}</td>
                  <td>{details.expiration}</td>
                  <td>{details.updated_at}</td>
                  <td>{details.input_tokens.toLocaleString()}</td>
                  <td>{details.output_tokens.toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </>
      )}
      <pre>{JSON.stringify(rsp, null, 2)}</pre>
    </>
  );
};

// Create a new component that will be meant for the reset password mode, it should have an email, new password and a button to reset the password
const AdminChangeEmail = ({ apiClient }) => {
  const [old_email, setOldEmail] = useState('');
  const [new_email, setNewEmail] = useState('');
  const [rsp, setRsp] = useState({ Status: 'Response will show here.' });

  const handleLookup = () => {
    setRsp({ Status: "Changing User's email..." });
    apiClient
      .adminChangeEmail(old_email, new_email)
      .then((response) => {
        setRsp(response); // Update rsp state with the response
      })
      .catch((error) => {
        console.error('Error changing user email:', error);
        setRsp({ Status: `Error changing user email - ${error}` });
        // Handle error (e.g., updating rsp with error message or a default message)
      });
  };

  return (
    <>
      <p>Enter User's current email and new email</p>
      <Form
        onSubmit={(e) => {
          e.preventDefault();
          handleLookup();
        }}
      >
        <FormGroup row>
          <Label for="Current email" sm={2}>
            Current Email
          </Label>
          <Col sm={10}>
            <Input
              name="email"
              id="email"
              placeholder="Current Email"
              value={old_email}
              onChange={(e) => setOldEmail(e.target.value)}
            />
          </Col>
        </FormGroup>
        <FormGroup row>
          <Label for="New Email" sm={2}>
            New Email
          </Label>
          <Col sm={10}>
            <Input
              name="New Email"
              id="New Email"
              placeholder="New Email"
              value={new_email}
              onChange={(e) => setNewEmail(e.target.value)}
            />
          </Col>
        </FormGroup>

        <FormGroup row>
          <Col sm={{ size: 10, offset: 2 }}>
            <Button color="primary" type="submit">
              Change Email
            </Button>
          </Col>
        </FormGroup>
      </Form>
      <pre>{JSON.stringify(rsp, null, 2)}</pre>
    </>
  );
};

const PartnerCredentials = ({ apiClient }) => {
  const [rsp, setRsp] = useState({ Status: 'Response will show here.' });
  const [secretKey, setSecretKey] = useState('');
  const [groupOrUser, setGroupOrUser] = useState('');
  const [force, setForce] = useState(false);
  const [initialBaseUrl] = useState(
    `https://sandbox${Math.floor(Math.random() * 1000)}.raintreeinc.com/dat/api`,
  );
  const [initialAppId] = useState(
    `5m${Math.random().toString(36).substring(2, 6)}-Mg5JXH4m-ucEmI-4Fr1HV`,
  );
  const [initialClientId] = useState(
    Math.random().toString(36).substring(2, 14),
  );
  const [initialClientSecret] = useState(
    Math.random().toString(36).substring(2, 34),
  );
  const [baseUrl, setBaseUrl] = useState('');
  const [appId, setAppId] = useState('');
  const [clientId, setClientId] = useState('');
  const [clientSecret, setClientSecret] = useState('');

  const handleCreateSecret = () => {
    if (!secretKey || !baseUrl || !appId || !clientId || !clientSecret) {
      alert('Please fill in all fields');
      return;
    }
    setRsp({ Status: 'Creating Partner Secret...' });
    apiClient
      .createPartnerSecret(
        secretKey,
        JSON.stringify({
          base_url: baseUrl,
          app_id: appId,
          client_id: clientId,
          client_secret: clientSecret,
        }),
        force,
      )
      .then(setRsp)
      .catch((error) => setRsp({ error }));
  };

  const handleCheckConnection = () => {
    setRsp({ Status: 'Checking Partner Connection...' });
    if (!secretKey) {
      alert('Secret Key is required to check connection');
      return;
    }
    apiClient
      .checkPartnerConnection(secretKey)
      .then(setRsp)
      .catch((error) => setRsp({ error }));
  };

  const handleLinkSecret = () => {
    setRsp({ Status: 'Linking Partner Secret...' });
    if (!secretKey || !groupOrUser) {
      alert('Please fill in Secret Key and Group/User ID');
      return;
    }
    apiClient
      .linkPartnerSecret(secretKey, groupOrUser)
      .then(setRsp)
      .catch((error) => setRsp({ error }));
  };

  return (
    <>
      <p>Create, check, and link partner secrets.</p>
      <Form>
        <FormGroup>
          <Label for="secretKey">Secret Key</Label>
          <Input
            id="secretKey"
            value={secretKey}
            onChange={(e) => setSecretKey(e.target.value)}
            placeholder="raintree_self_test"
          />
        </FormGroup>
        <FormGroup>
          <Label for="groupOrUser">Group Or User ID</Label>
          <Input
            id="groupOrUser"
            value={groupOrUser}
            onChange={(e) => setGroupOrUser(e.target.value)}
            placeholder="1223123 or name@domain.com"
          />
        </FormGroup>
        <fieldset style={{ marginLeft: '2rem', padding: '0rem' }}>
          <FormGroup>
            <Label for="baseUrl">Base URL</Label>
            <Input
              id="baseUrl"
              value={baseUrl}
              onChange={(e) => setBaseUrl(e.target.value)}
              placeholder={initialBaseUrl}
            />
          </FormGroup>
          <FormGroup>
            <Label for="appId">App ID</Label>
            <Input
              id="appId"
              value={appId}
              onChange={(e) => setAppId(e.target.value)}
              placeholder={initialAppId}
            />
          </FormGroup>
          <FormGroup>
            <Label for="clientId">Client ID</Label>
            <Input
              id="clientId"
              value={clientId}
              onChange={(e) => setClientId(e.target.value)}
              placeholder={initialClientId}
            />
          </FormGroup>
          <FormGroup>
            <Label for="clientSecret">Client Secret</Label>
            <Input
              id="clientSecret"
              value={clientSecret}
              onChange={(e) => setClientSecret(e.target.value)}
              placeholder={initialClientSecret}
            />
          </FormGroup>
        </fieldset>
        <FormGroup check>
          <Label check>
            <Input
              type="checkbox"
              checked={force}
              onChange={(e) => setForce(e.target.checked)}
            />
            Force
          </Label>
        </FormGroup>
        <div style={{ marginTop: '1rem' }}>
          <Button color="primary" onClick={handleCreateSecret}>
            Create Secret
          </Button>{' '}
          <Button color="primary" onClick={handleCheckConnection}>
            Check Connection
          </Button>{' '}
          <Button color="primary" onClick={handleLinkSecret}>
            Link Secret
          </Button>
        </div>
      </Form>
      <pre>{JSON.stringify(rsp, null, 2)}</pre>
    </>
  );
};

const FeatureManagement = ({ apiClient }) => {
  const [rsp, setRsp] = useState({ Status: 'Response will show here.' });
  const [emails, setEmails] = useState('');
  const [backendFeatures, setBackendFeatures] = useState({});
  const [userFeatures, setUserFeatures] = useState({});
  const [combinedFeatures, setCombinedFeatures] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [setFeatureName, setSetFeatureName] = useState('');
  const [setFeatureValue, setSetFeatureValue] = useState('');
  // add something like this{ "use_mic": [true, false], "use_speech": [true, false], "show_coding_tab": [true, false], "show_documentation_tab": [true, false], "show_notes_tab": [true, false] };
  const normalizeBackendFeatures = (raw) => {
    const source = raw && raw.features ? raw.features : raw || {};
    const normalized = {};
    Object.entries(source).forEach(([name, val]) => {
      normalized[name] = Array.isArray(val) ? val : [val];
    });
    return normalized;
  };

  const normalizeUserFeatures = (raw) => {
    // Accept either a direct map of feature -> value
    // or { user_features: {...} }
    if (!raw) return {};
    if (raw.user_features && typeof raw.user_features === 'object') {
      return raw.user_features;
    }
    if (typeof raw === 'object') {
      return raw;
    }
    return {};
  };

  // Segmented Control Component
  const SegmentedControl = ({
    options,
    selectedValue,
    onValueChange,
    featureName,
  }) => {
    const green = '#6bd888';
    const textColor = '#1f2937';
    return (
      <div style={{ display: 'inline-flex', alignItems: 'center' }}>
        {(options && options.length > 0 ? options : ['enabled']).map(
          (option, idx) => {
            const isSingle = !options || options.length <= 1;
            const isSelected =
              selectedValue === undefined
                ? false
                : isSingle
                  ? true
                  : String(selectedValue) === String(option);
            const radiusLeft = idx === 0 ? '6px' : '0px';
            const radiusRight = idx === options.length - 1 ? '6px' : '0px';
            return (
              <button
                key={String(option)}
                type="button"
                onClick={() => {
                  if (!isSingle) onValueChange(featureName, option);
                }}
                style={{
                  padding: '6px 12px',
                  border: `1px solid ${green}`,
                  borderLeftWidth: idx === 0 ? '1px' : '0px',
                  backgroundColor: isSelected ? green : '#ffffff',
                  color: isSelected ? '#ffffff' : textColor,
                  cursor: isSingle ? 'default' : 'pointer',
                  fontSize: '13px',
                  fontWeight: isSelected ? 600 : 500,
                  lineHeight: '20px',
                  borderTopLeftRadius: radiusLeft,
                  borderBottomLeftRadius: radiusLeft,
                  borderTopRightRadius: radiusRight,
                  borderBottomRightRadius: radiusRight,
                  margin: 0,
                  whiteSpace: 'nowrap',
                }}
                onMouseEnter={(e) => {
                  if (!isSelected && !isSingle)
                    e.target.style.backgroundColor = '#f4fff8';
                }}
                onMouseLeave={(e) => {
                  if (!isSelected && !isSingle)
                    e.target.style.backgroundColor = '#ffffff';
                }}
              >
                {String(option)}
              </button>
            );
          },
        )}
      </div>
    );
  };

  // Load backend features on component mount
  useEffect(() => {
    setIsLoading(true);
    setRsp({ Status: 'Loading all features...' });
    apiClient
      .adminGetBackendFeatures()
      .then((response) => {
        response = { ...response, ...FRONTEND_FEATURES };
        setBackendFeatures(normalizeBackendFeatures(response));
        setRsp({ Status: 'All features loaded successfully.' });
        setIsLoading(false);
      })
      .catch((error) => {
        console.error('Error loading all features:', error);
        setRsp({ Status: `Error loading all features: ${error}` });
        setIsLoading(false);
      });
  }, []);

  // Get user features
  const handleGetUserFeatures = () => {
    if (!emails.trim()) {
      setRsp({ Status: 'Please enter email(s) first.' });
      return;
    }
    if (emails.includes(',')) {
      setRsp({ Status: 'Please enter single email.' });
      return;
    }

    setIsLoading(true);
    setRsp({ Status: "Getting user's features..." });

    apiClient
      .adminGetUsersFeatures(emails)
      .then((response) => {
        //{"learn":true,"minimal_ui":true}
        setUserFeatures(normalizeUserFeatures(response));
        setRsp({ Status: 'User features loaded successfully.' });
        setIsLoading(false);
      })
      .catch((error) => {
        console.error('Error getting user features:', error);
        setRsp({ Status: `Error getting user features: ${error}` });
        setIsLoading(false);
      });
  };

  // Auto-combine when either set changes
  useEffect(() => {
    if (!backendFeatures || Object.keys(backendFeatures).length === 0) return;
    const combined = {};
    Object.keys(backendFeatures).forEach((featureName) => {
      const options = backendFeatures[featureName] || [];
      const rawCurrent =
        featureName in userFeatures ? userFeatures[featureName] : undefined; // don't preselect when only backend list is loaded
      // Match by string equivalence to handle boolean/string mismatches (true vs "true")
      const matched = options.find((o) => String(o) === String(rawCurrent));
      combined[featureName] = { options, currentValue: matched };
    });
    setCombinedFeatures(combined);
  }, [backendFeatures, userFeatures]);

  // Handle feature value change
  const handleFeatureChange = (featureName, newValue) => {
    if (!emails.trim()) {
      setRsp({ Status: 'Please enter email(s) first.' });
      return;
    }

    setRsp({ Status: `Setting ${featureName} to ${newValue}...` });
    const emailList = emails
      .split(',')
      .map((email) => email.trim())
      .filter((email) => email);

    // Update local state immediately for better UX
    setCombinedFeatures((prev) => ({
      ...prev,
      [featureName]: {
        ...prev[featureName],
        currentValue: newValue,
      },
    }));

    // Call API with list of emails
    apiClient
      .adminSetFeature(emailList, featureName, newValue)
      .then((response) => {
        setRsp({
          Status: `Successfully set ${featureName} to ${newValue} for all users.`,
        });
      })
      .catch((error) => {
        console.error('Error setting feature:', error);
        setRsp({ Status: `Error setting feature: ${error}` });
        // Revert the local state change on error
        setCombinedFeatures((prev) => ({
          ...prev,
          [featureName]: {
            ...prev[featureName],
            currentValue:
              userFeatures[featureName] || backendFeatures[featureName][0],
          },
        }));
      });
  };

  return (
    <>
      {/* Show available features */}
      {Object.keys(backendFeatures).length > 0 && (
        <div
          style={{
            marginBottom: '1.5rem',
            padding: '1rem',
            backgroundColor: '#f8f9fa',
            borderRadius: '8px',
            border: '1px solid #e9ecef',
          }}
        >
          <h6
            style={{
              marginBottom: '0.75rem',
              color: '#495057',
              fontWeight: '600',
            }}
          >
            Available Features:
          </h6>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
              gap: '12px',
            }}
          >
            {Object.entries(backendFeatures).map(([featureName, options]) => (
              <div
                key={featureName}
                style={{
                  padding: '8px 12px',
                  backgroundColor: '#ffffff',
                  border: '1px solid #dee2e6',
                  borderRadius: '6px',
                  fontSize: '13px',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                }}
              >
                <div
                  style={{
                    fontWeight: '600',
                    marginBottom: '4px',
                    color: '#343a40',
                  }}
                >
                  {featureName}
                  <FeatureInfo featureName={featureName} idPrefix="avail" />
                </div>
                <div style={{ color: '#6c757d', fontSize: '12px' }}>
                  Options:{' '}
                  {Array.isArray(options)
                    ? options.join(', ')
                    : String(options)}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <Form>
        <FormGroup row>
          <Label for="emailsInput" sm={2}>
            Email(s)
          </Label>
          <Col sm={10}>
            <Input
              type="text"
              name="emails"
              id="emailsInput"
              placeholder="Enter email(s), comma separated for multiple users for set/unset feature, just enter email for get user features"
              value={emails}
              onChange={(e) => setEmails(e.target.value)}
            />
          </Col>
        </FormGroup>

        <FormGroup row>
          <Col sm={{ size: 10, offset: 2 }}>
            <Button
              color="primary"
              onClick={handleGetUserFeatures}
              disabled={isLoading}
              style={{ minWidth: '140px' }}
            >
              {isLoading ? 'Loading...' : 'Get User Features'}
            </Button>
          </Col>
        </FormGroup>
      </Form>

      {/* Set Feature Section */}
      <div
        style={{
          marginTop: '2rem',
          padding: '1rem',
          backgroundColor: '#f8f9fa',
          borderRadius: '8px',
          border: '1px solid #e9ecef',
        }}
      >
        <h5 style={{ marginBottom: '1rem', color: '#495057' }}>Set Feature</h5>
        <Form>
          <FormGroup row>
            <Label for="featureNameInput" sm={2}>
              Feature Name
            </Label>
            <Col sm={4}>
              <Input
                type="text"
                name="featureName"
                id="featureNameInput"
                placeholder="Enter feature name"
                value={setFeatureName}
                onChange={(e) => setSetFeatureName(e.target.value)}
              />
            </Col>
            <Label for="featureValueInput" sm={2}>
              Feature Value
            </Label>
            <Col sm={4}>
              <Input
                type="text"
                name="featureValue"
                id="featureValueInput"
                placeholder="Enter feature value"
                value={setFeatureValue}
                onChange={(e) => setSetFeatureValue(e.target.value)}
              />
            </Col>
          </FormGroup>
          <FormGroup row>
            <Col sm={{ size: 10, offset: 2 }}>
              <Button
                color="success"
                onClick={() => {
                  if (!emails.trim()) {
                    setRsp({ Status: 'Please enter email(s) first.' });
                    return;
                  }
                  if (!setFeatureName.trim() || !setFeatureValue.trim()) {
                    setRsp({
                      Status: 'Please enter both feature name and value.',
                    });
                    return;
                  }
                  const emailList = emails
                    .split(',')
                    .map((email) => email.trim())
                    .filter((email) => email);
                  setRsp({
                    Status: `Setting ${setFeatureName} to ${setFeatureValue} for ${emailList.length} user(s)...`,
                  });
                  const featureValue =
                    setFeatureValue === 'true'
                      ? true
                      : setFeatureValue === 'false'
                        ? false
                        : setFeatureValue;
                  apiClient
                    .adminSetFeature(emailList, setFeatureName, featureValue)
                    .then(() =>
                      setRsp({
                        Status: `Successfully set ${setFeatureName} to ${featureValue} for all users.`,
                      }),
                    )
                    .catch((error) => setRsp({ Status: `Error: ${error}` }));
                }}
                disabled={
                  isLoading ||
                  !emails.trim() ||
                  !setFeatureName.trim() ||
                  !setFeatureValue.trim()
                }
                style={{ minWidth: '120px' }}
              >
                Set Feature
              </Button>
            </Col>
          </FormGroup>
        </Form>
      </div>

      {/* Feature Controls - only show when user features are loaded */}
      {Object.keys(combinedFeatures).length > 0 &&
        Object.keys(userFeatures).length > 0 && (
          <div style={{ marginTop: '2rem' }}>
            <h5>Feature Controls</h5>
            {Object.entries(combinedFeatures).map(
              ([featureName, featureData]) => (
                <div
                  key={featureName}
                  style={{
                    marginBottom: '0.5rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                  }}
                >
                  <Label
                    style={{
                      margin: 0,
                      fontWeight: '600',
                      fontSize: '0.95rem',
                      minWidth: '140px',
                    }}
                  >
                    {featureName}
                    <FeatureInfo featureName={featureName} idPrefix="ctrl" />
                  </Label>
                  <SegmentedControl
                    options={featureData.options}
                    selectedValue={featureData.currentValue}
                    onValueChange={handleFeatureChange}
                    featureName={featureName}
                  />
                </div>
              ),
            )}
          </div>
        )}

      {/* Fallback: show user features directly when backend options aren't loaded */}
      {Object.keys(combinedFeatures).length === 0 &&
        Object.keys(userFeatures).length > 0 && (
          <div style={{ marginTop: '1rem' }}>
            <h5>Current User Features</h5>
            {Object.entries(userFeatures).map(([featureName, value]) => (
              <div
                key={featureName}
                style={{
                  marginBottom: '0.5rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                }}
              >
                <Label
                  style={{
                    margin: 0,
                    fontWeight: '600',
                    fontSize: '0.95rem',
                    minWidth: '140px',
                  }}
                >
                  {featureName}
                </Label>
                <span
                  style={{
                    padding: '6px 12px',
                    border: '1px solid #e0e0e0',
                    borderRadius: '6px',
                    backgroundColor:
                      String(value) === 'true' ? '#6bd888' : '#f1f3f5',
                    color: String(value) === 'true' ? '#ffffff' : '#343a40',
                    fontWeight: 600,
                    fontSize: '13px',
                  }}
                >
                  {String(value)}
                </span>
              </div>
            ))}
          </div>
        )}

      <pre>{JSON.stringify(rsp, null, 2)}</pre>
    </>
  );
};

const ModifyPeople = ({ apiClient }) => {
  const ITOOLS_URL =
    'https://6g690gn5a2.execute-api.us-east-1.amazonaws.com/Prod';
  // const ITOOLS_URL = 'http://127.0.0.1:5002';
  apiClient.apiClient.defaults.baseURL = ITOOLS_URL;
  const MODIFY_PEOPLE_URL = 'pipedrive/modifyPeople';
  const [csvData, setCsvData] = useState([]);
  const [results, setResults] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [processAllAtOnce, setProcessAllAtOnce] = useState(false);
  const [rsp, setRsp] = useState({
    Status: 'Upload a CSV file to get started.',
  });

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const text = e.target.result;
        const sanitized = (text || '').replace(/^\uFEFF/, '');
        const lines = sanitized
          .split('\n')
          .map((l) => l.replace(/\r$/, ''))
          .filter((line) => line.trim());

        if (lines.length === 0) {
          setCsvData([]);
          setRsp({ Status: 'Empty CSV.' });
          return;
        }

        // Detect delimiter: prefer comma, but support tab-delimited files
        const commaCols = lines[0].split(',').length;
        const tabCols = lines[0].split('\t').length;
        const delimiter = tabCols > commaCols ? '\t' : ',';

        // Parse header if present (case-insensitive, any order)
        const rawHeader = lines[0].split(delimiter).map((h) =>
          h
            .replace(/^\uFEFF/, '')
            .trim()
            .replace(/^"|"$/g, ''),
        );
        const headerMap = {};
        rawHeader.forEach((h, idx) => {
          const key = h.toLowerCase();
          if (key.includes('email')) headerMap.email = idx;
          else if (key === 'name' || key.includes('full name'))
            headerMap.name = idx;
          else if (key.startsWith('label') || key.includes('tag'))
            headerMap.labels = idx;
        });

        const hasHeader =
          typeof headerMap.email !== 'undefined' ||
          typeof headerMap.labels !== 'undefined' ||
          typeof headerMap.name !== 'undefined';
        const dataLines = hasHeader ? lines.slice(1) : lines;

        const parsedData = dataLines
          .map((line, index) => {
            const cells = line
              .split(delimiter)
              .map((c) => c.trim().replace(/^"|"$/g, ''));

            const safeGet = (idx) =>
              idx >= 0 && idx < cells.length ? cells[idx] : '';
            let email = '';
            let name = '';
            let labelsCell = '';

            if (hasHeader) {
              email = safeGet(headerMap.email ?? -1) || '';
              name = safeGet(headerMap.name ?? -1) || '';
              if (typeof headerMap.labels === 'number') {
                labelsCell = safeGet(headerMap.labels) || '';
              } else {
                // If no explicit labels column found, assume any remaining trailing columns are labels
                const used = [headerMap.email, headerMap.name].filter(
                  (v) => typeof v === 'number',
                );
                const remaining = cells.filter((_, idx) => !used.includes(idx));
                labelsCell = remaining.join(';');
              }
            } else {
              // No header: support 1, 2, or 3+ columns gracefully
              if (cells.length >= 3) {
                email = cells[0] || '';
                name = cells[1] || '';
                labelsCell = cells.slice(2).join(';');
              } else if (cells.length === 2) {
                email = cells[0] || '';
                labelsCell = cells[1] || '';
              } else {
                email = cells[0] || '';
              }
            }

            return {
              id: index,
              email,
              name,
              labels: labelsCell
                ? labelsCell
                    .split(';')
                    .map((label) => label.trim())
                    .filter((label) => label)
                : [],
              status: null,
              labels_added: [],
              labels_failed: [],
              error: null,
            };
          })
          .filter((row) => row.email); // Filter out rows without email

        // If nothing parsed, provide a descriptive message to help fix headers/format
        if (parsedData.length === 0) {
          setCsvData([]);
          setResults([]);
          const mapping = {
            email: typeof headerMap.email === 'number' ? headerMap.email : null,
            name: typeof headerMap.name === 'number' ? headerMap.name : null,
            labels:
              typeof headerMap.labels === 'number' ? headerMap.labels : null,
          };
          const reason = hasHeader
            ? mapping.email === null
              ? "No 'email' column detected in header."
              : 'No valid email values found in rows.'
            : 'No header detected and no valid rows (missing email column or separator?).';

          setRsp({
            Status: 'No valid rows found in CSV.',
            reason,
            header_detected: hasHeader,
            detected_header: hasHeader ? rawHeader : null,
            column_mapping: mapping,
            expected_headers: [
              'email',
              'name (optional)',
              'labels | label | tags',
            ],
            tips: [
              'Columns must be comma-separated (CSV).',
              'Header names are case-insensitive; recognized: email, name, labels/label/tags.',
              'Separate multiple labels with semicolons: labelA;labelB.',
            ],
          });
          return;
        }

        setCsvData(parsedData);
        setResults([]);
        setRsp({
          Status: `Loaded ${parsedData.length} rows from CSV. Ready to process.`,
        });
      } catch (error) {
        setRsp({ Status: `Error parsing CSV: ${error.message}` });
      }
    };
    reader.readAsText(file);
  };

  const handleModifyPeople = async () => {
    if (csvData.length === 0) {
      setRsp({ Status: 'No data to process. Please upload a CSV file first.' });
      return;
    }

    setIsProcessing(true);
    setRsp({ Status: 'Processing people modifications...' });

    try {
      // Transform csvData to API format
      const people = csvData.map((row) => {
        const p = { email: row.email, labels: row.labels };
        if (row.name && row.name.length > 0) {
          p.name = row.name;
        }
        return p;
      });

      const allResults = [];
      let batchesCompleted = 0;

      // Determine batch size based on user preference
      let batchSize;
      if (processAllAtOnce) {
        // Process all at once
        batchSize = people.length;
      } else {
        // Static batch size of 100
        batchSize = 100;
      }

      let cursor = 0;
      while (cursor < people.length) {
        const currentBatch = batchesCompleted + 1;
        const batch = people.slice(cursor, cursor + batchSize);

        // Update status to show current batch progress
        if (processAllAtOnce) {
          setRsp({
            Status: `Processing all ${people.length} people at once...`,
          });
        } else {
          setRsp({
            Status: `Processing batch ${currentBatch} (${cursor + 1}-${Math.min(cursor + batch.length, people.length)} of ${people.length} people)...`,
          });
        }

        const response = await apiClient.JMPost(MODIFY_PEOPLE_URL, {
          people: batch,
        });

        if (response.result) {
          allResults.push(...response.result);

          // Update csvData progressively after each batch
          setCsvData((prevData) => {
            return prevData.map((row) => {
              const result = response.result.find((r) => r.email === row.email);
              if (result) {
                const computedStatus =
                  result.status ||
                  result.result ||
                  (result.labels_failed && result.labels_failed.length > 0
                    ? result.labels_added && result.labels_added.length > 0
                      ? 'partial_failure'
                      : 'error'
                    : 'success');
                return {
                  ...row,
                  status: computedStatus,
                  labels_added: result.labels_added || [],
                  labels_failed: result.labels_failed || [],
                  error: result.error || result.comment || null,
                };
              }
              return row;
            });
          });

          // Update status with batch completion
          const processedCount = allResults.length;
          batchesCompleted += 1;
          if (!processAllAtOnce) {
            setRsp({
              Status: `Completed batch ${currentBatch}. Processed ${processedCount}/${people.length}...`,
              partial_results: allResults,
            });
          }
        }

        // Advance cursor by exactly what we sent this iteration
        cursor += batch.length;
      }

      const response = { result: allResults };

      if (response.result) {
        setResults(response.result);
        setRsp({
          Status: `Successfully processed all ${response.result.length} people.`,
          ...response,
        });
      } else {
        setRsp({ Status: 'No results returned from API', ...response });
      }
    } catch (error) {
      console.error('Error modifying people:', error);
      setRsp({ Status: `Error: ${error.message}` });
    } finally {
      setIsProcessing(false);
    }
  };

  const downloadResultsCsv = () => {
    try {
      const headers = [
        'email',
        'name',
        'labels',
        'status',
        'labels_added',
        'labels_failed',
        'notes',
      ];
      const lines = [headers.join(',')];
      csvData.forEach((row) => {
        const values = [
          row.email || '',
          row.name || '',
          (row.labels || []).join(';'),
          row.status || '',
          (row.labels_added || []).join(';'),
          (row.labels_failed || []).join(';'),
          row.error || '',
        ];
        const safe = values
          .map((v) => String(v).replace(/"/g, '""'))
          .map((v) => `"${v}"`)
          .join(',');
        lines.push(safe);
      });
      const blob = new Blob([lines.join('\n')], {
        type: 'text/csv;charset=utf-8;',
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'modify_people_results.csv');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (e) {
      setRsp({ Status: `Error creating CSV: ${e.message}` });
    }
  };

  return (
    <>
      <div style={{ fontSize: '0.95rem', marginBottom: '0.25rem' }}>
        Upload a CSV to add labels in Pipedrive. Format:{' '}
        <code>email,name,labels</code>
        (labels separated by semicolons). If not found and a name is provided, a
        person will be created.
      </div>
      <details style={{ marginBottom: '0.5rem' }}>
        <summary style={{ cursor: 'pointer' }}>Example CSV</summary>
        <div style={{ paddingTop: '0.25rem' }}>
          <code>
            email,name,labels
            <br />
            user@example.com,Jane Doe,label1;label2
            <br />
            user2@example.com,,label3
          </code>
        </div>
      </details>

      <Form>
        <FormGroup row style={{ marginBottom: '0.5rem' }}>
          <Label for="csvFile" sm={2} style={{ marginBottom: 0 }}>
            CSV File
          </Label>
          <Col sm={10}>
            <div
              style={{
                display: 'flex',
                gap: '8px',
                alignItems: 'center',
                flexWrap: 'wrap',
              }}
            >
              <Input
                type="file"
                name="csvFile"
                id="csvFile"
                accept=".csv,.txt"
                onChange={handleFileUpload}
                style={{ maxWidth: '360px' }}
              />
              <Button
                size="sm"
                color="secondary"
                onClick={() => {
                  const sample = [
                    'email,name,labels',
                    'user@example.com,Jane Doe,Label A;Label B',
                    'user2@example.com,,Label C',
                  ].join('\n');
                  const blob = new Blob([sample], {
                    type: 'text/csv;charset=utf-8;',
                  });
                  const url = URL.createObjectURL(blob);
                  const link = document.createElement('a');
                  link.href = url;
                  link.setAttribute('download', 'modify_people_sample.csv');
                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);
                  URL.revokeObjectURL(url);
                }}
              >
                Download sample
              </Button>
              <Button
                size="sm"
                color="secondary"
                onClick={downloadResultsCsv}
                disabled={csvData.length === 0}
              >
                Download results
              </Button>
            </div>
          </Col>
        </FormGroup>

        <FormGroup row style={{ marginBottom: '0.5rem' }}>
          <Col sm={{ size: 10, offset: 2 }}>
            <FormGroup check style={{ marginBottom: '0.5rem' }}>
              <Label check>
                <Input
                  type="checkbox"
                  checked={processAllAtOnce}
                  onChange={(e) => setProcessAllAtOnce(e.target.checked)}
                  disabled={isProcessing}
                />{' '}
                Process all at once (otherwise 100 at a time) (This may
                timeout...but it will work, give it ~3 minutes and you should
                see the results reflected in pipedrive)
              </Label>
            </FormGroup>
            <Button
              color="primary"
              size="sm"
              onClick={handleModifyPeople}
              disabled={isProcessing || csvData.length === 0}
            >
              {isProcessing ? 'Processing...' : 'Send to Pipedrive'}
            </Button>
            <span
              style={{
                marginLeft: '8px',
                fontSize: '0.9rem',
                color: '#6c757d',
              }}
            >
              {csvData.length > 0 ? `${csvData.length} rows loaded` : ''}
            </span>
          </Col>
        </FormGroup>
      </Form>

      {csvData.length > 0 && (
        <>
          {rsp && rsp.Status && rsp.Status.includes('Processing batch') && (
            <div
              className="alert alert-info"
              role="alert"
              style={{
                marginBottom: '0.5rem',
                padding: '6px 10px',
                fontSize: '0.9rem',
              }}
            >
              {rsp.Status}
            </div>
          )}
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'baseline',
            }}
          >
            <h5 style={{ marginBottom: '0.5rem' }}>Data Preview & Results</h5>
            {rsp && rsp.Status && !rsp.Status.includes('Processing batch') && (
              <span style={{ fontSize: '0.9rem', color: '#6c757d' }}>
                {rsp.Status}
              </span>
            )}
          </div>
          <Table
            striped
            className="table-sm"
            style={{ marginBottom: '0.5rem' }}
          >
            <thead>
              <tr>
                <th>#</th>
                <th>Email</th>
                <th>Name</th>
                <th>Labels to Add</th>
                <th>Status</th>
                <th>Labels Added</th>
                <th>Labels Failed</th>
                <th>Notes</th>
              </tr>
            </thead>
            <tbody>
              {csvData.map((row, index) => (
                <tr
                  key={row.id}
                  className={
                    row.status === 'success'
                      ? 'table-success'
                      : row.status === 'error'
                        ? 'table-danger'
                        : row.status === 'partial_failure'
                          ? 'table-warning'
                          : ''
                  }
                >
                  <td>{index + 1}</td>
                  <td>{row.email}</td>
                  <td>{row.name}</td>
                  <td>{row.labels.join(', ')}</td>
                  <td>
                    {row.status && (
                      <span
                        className={`badge badge-${
                          row.status === 'success'
                            ? 'success'
                            : row.status === 'error'
                              ? 'danger'
                              : row.status === 'partial_failure'
                                ? 'warning'
                                : 'secondary'
                        }`}
                      >
                        {row.status}
                      </span>
                    )}
                  </td>
                  <td>{row.labels_added.join(', ')}</td>
                  <td>{row.labels_failed.join(', ')}</td>
                  <td>{row.error}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </>
      )}

      <pre>{JSON.stringify(rsp, null, 2)}</pre>
    </>
  );
};

const ChartMogulCRUD = ({ apiClient }) => {
  const [rsp, setRsp] = useState({ Status: 'Select an operation above.' });
  const [operation, setOperation] = useState('create');

  // Create state
  const [createResellerName, setCreateResellerName] = useState('WebPT');
  const [createResellerNameOther, setCreateResellerNameOther] = useState('');
  const [createGroupSubscriptionId, setCreateGroupSubscriptionId] =
    useState('');
  const [createOrganizationName, setCreateOrganizationName] = useState('');
  const [createSubscriberEmail, setCreateSubscriberEmail] = useState('');
  const [createSeats, setCreateSeats] = useState('');
  const [createExpiration, setCreateExpiration] = useState('');
  const [createPricePerSeat, setCreatePricePerSeat] = useState('');
  const [createEventDate, setCreateEventDate] = useState(new Date());

  // Update state
  const [updateResellerName, setUpdateResellerName] = useState('WebPT');
  const [updateResellerNameOther, setUpdateResellerNameOther] = useState('');
  const [updateGroupSubscriptionId, setUpdateGroupSubscriptionId] =
    useState('');
  const [updateSubscriberEmail, setUpdateSubscriberEmail] = useState('');
  const [updateNewSeats, setUpdateNewSeats] = useState('');
  const [updateNewOrganizationName, setUpdateNewOrganizationName] =
    useState('');
  const [updateNewExpiration, setUpdateNewExpiration] = useState('');
  const [updateEventDate, setUpdateEventDate] = useState(new Date());

  // Seat Increase state
  const [seatIncreaseResellerName, setSeatIncreaseResellerName] =
    useState('WebPT');
  const [seatIncreaseResellerNameOther, setSeatIncreaseResellerNameOther] =
    useState('');
  const [seatIncreaseGroupSubscriptionId, setSeatIncreaseGroupSubscriptionId] =
    useState('');
  const [seatIncreaseNewSeatCount, setSeatIncreaseNewSeatCount] = useState('');
  const [seatIncreaseSubscriberEmail, setSeatIncreaseSubscriberEmail] =
    useState('');
  const [seatIncreaseOldSeatCount, setSeatIncreaseOldSeatCount] = useState('');
  const [seatIncreaseEventDate, setSeatIncreaseEventDate] = useState(
    new Date(),
  );

  // Cancel Subscription state (creates cancellation event, doesn't delete records)
  const [cancelResellerName, setCancelResellerName] = useState('WebPT');
  const [cancelResellerNameOther, setCancelResellerNameOther] = useState('');
  const [cancelGroupSubscriptionId, setCancelGroupSubscriptionId] =
    useState('');
  const [cancelSubscriberEmail, setCancelSubscriberEmail] = useState('');
  const [cancelEventDate, setCancelEventDate] = useState(new Date());

  // Delete state (actually deletes records)
  const [deleteSubscriptionId, setDeleteSubscriptionId] = useState('');
  const [deleteSubscriberEmail, setDeleteSubscriberEmail] = useState('');

  // Issue Invoices state
  const [issueInvoicesEventDate, setIssueInvoicesEventDate] = useState(
    new Date(),
  );

  const handleCreate = () => {
    setRsp({ Status: 'Creating subscription...' });
    const resellerName =
      createResellerName === 'Other'
        ? createResellerNameOther
        : createResellerName;
    const payload = {
      reseller_name: resellerName,
      group_subscription_id: createGroupSubscriptionId,
      organization_name: createOrganizationName,
      subscriber_email: createSubscriberEmail,
      seats: parseInt(createSeats),
      event_date: createEventDate.toISOString().split('T')[0],
    };
    if (createExpiration) {
      payload.expiration = createExpiration;
    }
    if (createPricePerSeat) {
      payload.price_per_seat = parseFloat(createPricePerSeat);
    }
    apiClient
      .chartMogulCreateSubscription(payload)
      .then((response) => {
        setRsp(response);
      })
      .catch((error) => {
        console.error('Error creating subscription:', error);
        setRsp({ Status: `Error creating subscription: ${error}` });
      });
  };

  const handleUpdate = () => {
    setRsp({ Status: 'Updating subscription...' });
    const resellerName =
      updateResellerName === 'Other'
        ? updateResellerNameOther
        : updateResellerName;
    const changes = {};
    if (updateNewSeats) {
      changes.new_seats = parseInt(updateNewSeats);
    }
    if (updateNewOrganizationName) {
      changes.new_organization_name = updateNewOrganizationName;
    }
    if (updateNewExpiration) {
      changes.new_expiration = updateNewExpiration;
    }
    const payload = {
      reseller_name: resellerName,
      group_subscription_id: updateGroupSubscriptionId,
      subscriber_email: updateSubscriberEmail,
      changes: changes,
      event_date: updateEventDate.toISOString().split('T')[0],
    };
    apiClient
      .chartMogulUpdateSubscription(payload)
      .then((response) => {
        setRsp(response);
      })
      .catch((error) => {
        console.error('Error updating subscription:', error);
        setRsp({ Status: `Error updating subscription: ${error}` });
      });
  };

  const handleSeatIncrease = () => {
    setRsp({ Status: 'Processing seat increase...' });
    const resellerName =
      seatIncreaseResellerName === 'Other'
        ? seatIncreaseResellerNameOther
        : seatIncreaseResellerName;
    const payload = {
      reseller_name: resellerName,
      group_subscription_id: seatIncreaseGroupSubscriptionId,
      new_seat_count: parseInt(seatIncreaseNewSeatCount),
      subscriber_email: seatIncreaseSubscriberEmail,
      event_date: seatIncreaseEventDate.toISOString().split('T')[0],
    };
    if (seatIncreaseOldSeatCount) {
      payload.old_seat_count = parseInt(seatIncreaseOldSeatCount);
    }
    apiClient
      .chartMogulSeatIncrease(payload)
      .then((response) => {
        setRsp(response);
      })
      .catch((error) => {
        console.error('Error processing seat increase:', error);
        setRsp({ Status: `Error processing seat increase: ${error}` });
      });
  };

  const handleCancel = () => {
    setRsp({ Status: 'Cancelling subscription...' });
    const resellerName =
      cancelResellerName === 'Other'
        ? cancelResellerNameOther
        : cancelResellerName;
    const payload = {
      reseller_name: resellerName,
      group_subscription_id: cancelGroupSubscriptionId,
      subscriber_email: cancelSubscriberEmail,
      event_date: cancelEventDate.toISOString().split('T')[0],
    };
    apiClient
      .chartMogulCancelSubscription(payload)
      .then((response) => {
        setRsp(response);
      })
      .catch((error) => {
        console.error('Error cancelling subscription:', error);
        setRsp({ Status: `Error cancelling subscription: ${error}` });
      });
  };

  const handleDelete = () => {
    setRsp({ Status: 'Deleting subscription records...' });
    apiClient
      .chartMogulDeleteSubscription(
        parseInt(deleteSubscriptionId),
        deleteSubscriberEmail || null,
      )
      .then((response) => {
        setRsp(response);
      })
      .catch((error) => {
        console.error('Error deleting subscription:', error);
        setRsp({ Status: `Error deleting subscription: ${error}` });
      });
  };

  const handleIssueInvoices = () => {
    setRsp({ Status: 'Issuing due invoices...' });
    apiClient
      .chartMogulIssueDueInvoices(
        issueInvoicesEventDate.toISOString().split('T')[0],
      )
      .then((response) => {
        setRsp(response);
      })
      .catch((error) => {
        console.error('Error issuing invoices:', error);
        setRsp({ Status: `Error issuing invoices: ${error}` });
      });
  };

  const operationLabels = {
    create: 'Create Subscription',
    update: 'Update Subscription',
    seatIncrease: 'Seat Increase',
    cancel: 'Cancel Subscription',
    delete: 'Delete Subscription Records',
    issueInvoices: 'Issue Due Invoices',
  };

  return (
    <>
      <div style={{ marginBottom: '1rem' }}>
        <h6
          style={{ color: '#525f7f', marginBottom: '0.25rem', fontWeight: 600 }}
        >
          ChartMogul Subscription Management
        </h6>
        <p style={{ color: '#8898aa', marginBottom: 0, fontSize: '0.875rem' }}>
          Manage ChartMogul subscriptions: Create, Update, Delete subscriptions,
          Process seat increases, and Issue due invoices.
        </p>
      </div>

      <Card
        className="shadow-sm"
        style={{ marginBottom: '1rem', border: '1px solid #e9ecef' }}
      >
        <CardHeader
          style={{
            backgroundColor: '#f8f9fa',
            borderBottom: '1px solid #e9ecef',
            padding: '0.5rem 1rem',
          }}
        >
          <FormGroup style={{ marginBottom: 0 }}>
            <Label
              for="operationSelect"
              style={{
                fontWeight: 600,
                color: '#525f7f',
                marginBottom: '0.25rem',
                fontSize: '0.875rem',
              }}
            >
              Operation Type
            </Label>
            <Input
              type="select"
              name="operation"
              id="operationSelect"
              value={operation}
              onChange={(e) => setOperation(e.target.value)}
              style={{
                fontSize: '0.875rem',
                padding: '0.5rem',
                border: '1px solid #cad1d7',
                borderRadius: '0.375rem',
              }}
            >
              <option value="create">✨ Create Subscription</option>
              <option value="update">📝 Update Subscription</option>
              <option value="seatIncrease">📈 Seat Increase</option>
              <option value="cancel">❌ Cancel Subscription</option>
              <option value="delete">🗑️ Delete Subscription Records</option>
              <option value="issueInvoices">💰 Issue Due Invoices</option>
            </Input>
          </FormGroup>
        </CardHeader>
      </Card>

      {/* Create Form */}
      {operation === 'create' && (
        <Card
          className="shadow-sm"
          style={{ border: '1px solid #e9ecef', marginBottom: '1rem' }}
        >
          <CardHeader
            style={{
              backgroundColor: '#e3f2fd',
              borderBottom: '1px solid #e9ecef',
              padding: '0.5rem 1rem',
            }}
          >
            <h6
              style={{
                margin: 0,
                color: '#1976d2',
                fontWeight: 600,
                fontSize: '0.875rem',
              }}
            >
              ✨ {operationLabels.create}
            </h6>
          </CardHeader>
          <div style={{ padding: '1rem' }}>
            <Row>
              <Col md={6}>
                <FormGroup style={{ marginBottom: '0.75rem' }}>
                  <Label
                    for="createResellerName"
                    style={{
                      fontWeight: 500,
                      color: '#525f7f',
                      fontSize: '0.875rem',
                      marginBottom: '0.25rem',
                    }}
                  >
                    Reseller Name <span style={{ color: '#e74c3c' }}>*</span>
                  </Label>
                  <Input
                    type="select"
                    name="createResellerName"
                    id="createResellerName"
                    value={createResellerName}
                    onChange={(e) => setCreateResellerName(e.target.value)}
                    style={{
                      border: '1px solid #cad1d7',
                      borderRadius: '0.375rem',
                      fontSize: '0.875rem',
                      padding: '0.5rem',
                    }}
                    bsSize="sm"
                  >
                    <option value="WebPT">WebPT</option>
                    <option value="Stride">Stride</option>
                    <option value="Empower">Empower</option>
                    <option value="Other">Other</option>
                  </Input>
                  {createResellerName === 'Other' && (
                    <Input
                      type="text"
                      name="createResellerNameOther"
                      id="createResellerNameOther"
                      placeholder="Enter reseller name"
                      value={createResellerNameOther}
                      onChange={(e) =>
                        setCreateResellerNameOther(e.target.value)
                      }
                      style={{
                        border: '1px solid #cad1d7',
                        borderRadius: '0.375rem',
                        fontSize: '0.875rem',
                        padding: '0.5rem',
                        marginTop: '0.5rem',
                      }}
                      bsSize="sm"
                    />
                  )}
                </FormGroup>
                <FormGroup style={{ marginBottom: '0.75rem' }}>
                  <Label
                    for="createGroupSubscriptionId"
                    style={{
                      fontWeight: 500,
                      color: '#525f7f',
                      fontSize: '0.875rem',
                      marginBottom: '0.25rem',
                    }}
                  >
                    Group Subscription ID{' '}
                    <span style={{ color: '#e74c3c' }}>*</span>
                  </Label>
                  <Input
                    type="number"
                    name="createGroupSubscriptionId"
                    id="createGroupSubscriptionId"
                    placeholder="123456"
                    value={createGroupSubscriptionId}
                    onChange={(e) =>
                      setCreateGroupSubscriptionId(e.target.value)
                    }
                    style={{
                      border: '1px solid #cad1d7',
                      borderRadius: '0.375rem',
                      fontSize: '0.875rem',
                      padding: '0.5rem',
                    }}
                    bsSize="sm"
                  />
                </FormGroup>
                <FormGroup style={{ marginBottom: '0.75rem' }}>
                  <Label
                    for="createOrganizationName"
                    style={{
                      fontWeight: 500,
                      color: '#525f7f',
                      fontSize: '0.875rem',
                      marginBottom: '0.25rem',
                    }}
                  >
                    Organization Name{' '}
                    <span style={{ color: '#e74c3c' }}>*</span>
                  </Label>
                  <Input
                    type="text"
                    name="createOrganizationName"
                    id="createOrganizationName"
                    placeholder="Company Name"
                    value={createOrganizationName}
                    onChange={(e) => setCreateOrganizationName(e.target.value)}
                    style={{
                      border: '1px solid #cad1d7',
                      borderRadius: '0.375rem',
                      fontSize: '0.875rem',
                      padding: '0.5rem',
                    }}
                    bsSize="sm"
                  />
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup style={{ marginBottom: '0.75rem' }}>
                  <Label
                    for="createSubscriberEmail"
                    style={{
                      fontWeight: 500,
                      color: '#525f7f',
                      fontSize: '0.875rem',
                      marginBottom: '0.25rem',
                    }}
                  >
                    Subscriber Email <span style={{ color: '#e74c3c' }}>*</span>
                  </Label>
                  <Input
                    type="email"
                    name="createSubscriberEmail"
                    id="createSubscriberEmail"
                    placeholder="subscriber@example.com"
                    value={createSubscriberEmail}
                    onChange={(e) => setCreateSubscriberEmail(e.target.value)}
                    style={{
                      border: '1px solid #cad1d7',
                      borderRadius: '0.375rem',
                      fontSize: '0.875rem',
                      padding: '0.5rem',
                    }}
                    bsSize="sm"
                  />
                </FormGroup>
                <FormGroup style={{ marginBottom: '0.75rem' }}>
                  <Label
                    for="createSeats"
                    style={{
                      fontWeight: 500,
                      color: '#525f7f',
                      fontSize: '0.875rem',
                      marginBottom: '0.25rem',
                    }}
                  >
                    Seats <span style={{ color: '#e74c3c' }}>*</span>
                  </Label>
                  <Input
                    type="number"
                    name="createSeats"
                    id="createSeats"
                    placeholder="10"
                    value={createSeats}
                    onChange={(e) => setCreateSeats(e.target.value)}
                    style={{
                      border: '1px solid #cad1d7',
                      borderRadius: '0.375rem',
                      fontSize: '0.875rem',
                      padding: '0.5rem',
                    }}
                    bsSize="sm"
                  />
                </FormGroup>
                <FormGroup style={{ marginBottom: '0.75rem' }}>
                  <Label
                    for="createExpiration"
                    style={{
                      fontWeight: 500,
                      color: '#525f7f',
                      fontSize: '0.875rem',
                      marginBottom: '0.25rem',
                    }}
                  >
                    Expiration
                  </Label>
                  <Input
                    type="text"
                    name="createExpiration"
                    id="createExpiration"
                    placeholder="YYYY-MM-DD"
                    value={createExpiration}
                    onChange={(e) => setCreateExpiration(e.target.value)}
                    style={{
                      border: '1px solid #cad1d7',
                      borderRadius: '0.375rem',
                      fontSize: '0.875rem',
                      padding: '0.5rem',
                    }}
                    bsSize="sm"
                  />
                </FormGroup>
                <FormGroup style={{ marginBottom: '0.75rem' }}>
                  <Label
                    for="createPricePerSeat"
                    style={{
                      fontWeight: 500,
                      color: '#525f7f',
                      fontSize: '0.875rem',
                      marginBottom: '0.25rem',
                    }}
                  >
                    Price Per Seat
                  </Label>
                  <Input
                    type="number"
                    step="0.01"
                    name="createPricePerSeat"
                    id="createPricePerSeat"
                    placeholder="29.99"
                    value={createPricePerSeat}
                    onChange={(e) => setCreatePricePerSeat(e.target.value)}
                    style={{
                      border: '1px solid #cad1d7',
                      borderRadius: '0.375rem',
                      fontSize: '0.875rem',
                      padding: '0.5rem',
                    }}
                    bsSize="sm"
                  />
                </FormGroup>
                <FormGroup style={{ marginBottom: '0.75rem' }}>
                  <Label
                    for="createEventDate"
                    style={{
                      fontWeight: 500,
                      color: '#525f7f',
                      fontSize: '0.875rem',
                      marginBottom: '0.25rem',
                    }}
                  >
                    Event Date
                  </Label>
                  <DatePicker
                    selected={createEventDate}
                    onChange={(date) => setCreateEventDate(date)}
                    dateFormat="yyyy-MM-dd"
                    className="form-control form-control-sm"
                    style={{
                      border: '1px solid #cad1d7',
                      borderRadius: '0.375rem',
                      padding: '0.375rem',
                      fontSize: '0.875rem',
                    }}
                  />
                </FormGroup>
              </Col>
            </Row>
            <div
              style={{
                display: 'flex',
                justifyContent: 'flex-end',
                paddingTop: '0.75rem',
                borderTop: '1px solid #e9ecef',
                marginTop: '0.5rem',
              }}
            >
              <Button
                color="primary"
                onClick={handleCreate}
                size="sm"
                style={{
                  padding: '0.5rem 1.5rem',
                  fontSize: '0.875rem',
                  fontWeight: 600,
                  borderRadius: '0.375rem',
                }}
              >
                Create Subscription
              </Button>
            </div>
          </div>
        </Card>
      )}

      {/* Update Form */}
      {operation === 'update' && (
        <Card
          className="shadow-sm"
          style={{ border: '1px solid #e9ecef', marginBottom: '1rem' }}
        >
          <CardHeader
            style={{
              backgroundColor: '#fff3cd',
              borderBottom: '1px solid #e9ecef',
              padding: '0.5rem 1rem',
            }}
          >
            <h6
              style={{
                margin: 0,
                color: '#856404',
                fontWeight: 600,
                fontSize: '0.875rem',
              }}
            >
              📝 {operationLabels.update}
            </h6>
          </CardHeader>
          <div style={{ padding: '1rem' }}>
            <Row>
              <Col md={6}>
                <FormGroup style={{ marginBottom: '0.75rem' }}>
                  <Label
                    for="updateResellerName"
                    style={{
                      fontWeight: 500,
                      color: '#525f7f',
                      fontSize: '0.875rem',
                      marginBottom: '0.25rem',
                    }}
                  >
                    Reseller Name <span style={{ color: '#e74c3c' }}>*</span>
                  </Label>
                  <Input
                    type="select"
                    name="updateResellerName"
                    id="updateResellerName"
                    value={updateResellerName}
                    onChange={(e) => setUpdateResellerName(e.target.value)}
                    style={{
                      border: '1px solid #cad1d7',
                      borderRadius: '0.375rem',
                      fontSize: '0.875rem',
                      padding: '0.5rem',
                    }}
                    bsSize="sm"
                  >
                    <option value="WebPT">WebPT</option>
                    <option value="Stride">Stride</option>
                    <option value="Empower">Empower</option>
                    <option value="Other">Other</option>
                  </Input>
                  {updateResellerName === 'Other' && (
                    <Input
                      type="text"
                      name="updateResellerNameOther"
                      id="updateResellerNameOther"
                      placeholder="Enter reseller name"
                      value={updateResellerNameOther}
                      onChange={(e) =>
                        setUpdateResellerNameOther(e.target.value)
                      }
                      style={{
                        border: '1px solid #cad1d7',
                        borderRadius: '0.375rem',
                        fontSize: '0.875rem',
                        padding: '0.5rem',
                        marginTop: '0.5rem',
                      }}
                      bsSize="sm"
                    />
                  )}
                </FormGroup>
                <FormGroup style={{ marginBottom: '0.75rem' }}>
                  <Label
                    for="updateGroupSubscriptionId"
                    style={{
                      fontWeight: 500,
                      color: '#525f7f',
                      fontSize: '0.875rem',
                      marginBottom: '0.25rem',
                    }}
                  >
                    Group Subscription ID{' '}
                    <span style={{ color: '#e74c3c' }}>*</span>
                  </Label>
                  <Input
                    type="number"
                    name="updateGroupSubscriptionId"
                    id="updateGroupSubscriptionId"
                    placeholder="123456"
                    value={updateGroupSubscriptionId}
                    onChange={(e) =>
                      setUpdateGroupSubscriptionId(e.target.value)
                    }
                    style={{
                      border: '1px solid #cad1d7',
                      borderRadius: '0.375rem',
                      fontSize: '0.875rem',
                      padding: '0.5rem',
                    }}
                    bsSize="sm"
                  />
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup style={{ marginBottom: '0.75rem' }}>
                  <Label
                    for="updateSubscriberEmail"
                    style={{
                      fontWeight: 500,
                      color: '#525f7f',
                      fontSize: '0.875rem',
                      marginBottom: '0.25rem',
                    }}
                  >
                    Subscriber Email <span style={{ color: '#e74c3c' }}>*</span>
                  </Label>
                  <Input
                    type="email"
                    name="updateSubscriberEmail"
                    id="updateSubscriberEmail"
                    placeholder="subscriber@example.com"
                    value={updateSubscriberEmail}
                    onChange={(e) => setUpdateSubscriberEmail(e.target.value)}
                    style={{
                      border: '1px solid #cad1d7',
                      borderRadius: '0.375rem',
                      fontSize: '0.875rem',
                      padding: '0.5rem',
                    }}
                    bsSize="sm"
                  />
                </FormGroup>
                <FormGroup style={{ marginBottom: '0.75rem' }}>
                  <Label
                    for="updateEventDate"
                    style={{
                      fontWeight: 500,
                      color: '#525f7f',
                      fontSize: '0.875rem',
                      marginBottom: '0.25rem',
                    }}
                  >
                    Event Date
                  </Label>
                  <DatePicker
                    selected={updateEventDate}
                    onChange={(date) => setUpdateEventDate(date)}
                    dateFormat="yyyy-MM-dd"
                    className="form-control form-control-sm"
                    style={{
                      border: '1px solid #cad1d7',
                      borderRadius: '0.375rem',
                      padding: '0.375rem',
                      fontSize: '0.875rem',
                    }}
                  />
                </FormGroup>
              </Col>
            </Row>
            <hr style={{ margin: '0.75rem 0', borderColor: '#e9ecef' }} />
            <h6
              style={{
                color: '#8898aa',
                fontSize: '0.875rem',
                fontWeight: 600,
                marginBottom: '0.75rem',
              }}
            >
              Changes (leave blank to skip)
            </h6>
            <Row>
              <Col md={4}>
                <FormGroup style={{ marginBottom: '0.75rem' }}>
                  <Label
                    for="updateNewSeats"
                    style={{
                      fontWeight: 500,
                      color: '#525f7f',
                      fontSize: '0.875rem',
                      marginBottom: '0.25rem',
                    }}
                  >
                    New Seats
                  </Label>
                  <Input
                    type="number"
                    name="updateNewSeats"
                    id="updateNewSeats"
                    placeholder="15"
                    value={updateNewSeats}
                    onChange={(e) => setUpdateNewSeats(e.target.value)}
                    style={{
                      border: '1px solid #cad1d7',
                      borderRadius: '0.375rem',
                      fontSize: '0.875rem',
                      padding: '0.5rem',
                    }}
                    bsSize="sm"
                  />
                </FormGroup>
              </Col>
              <Col md={4}>
                <FormGroup style={{ marginBottom: '0.75rem' }}>
                  <Label
                    for="updateNewOrganizationName"
                    style={{
                      fontWeight: 500,
                      color: '#525f7f',
                      fontSize: '0.875rem',
                      marginBottom: '0.25rem',
                    }}
                  >
                    New Organization Name
                  </Label>
                  <Input
                    type="text"
                    name="updateNewOrganizationName"
                    id="updateNewOrganizationName"
                    placeholder="New Company Name"
                    value={updateNewOrganizationName}
                    onChange={(e) =>
                      setUpdateNewOrganizationName(e.target.value)
                    }
                    style={{
                      border: '1px solid #cad1d7',
                      borderRadius: '0.375rem',
                      fontSize: '0.875rem',
                      padding: '0.5rem',
                    }}
                    bsSize="sm"
                  />
                </FormGroup>
              </Col>
              <Col md={4}>
                <FormGroup style={{ marginBottom: '0.75rem' }}>
                  <Label
                    for="updateNewExpiration"
                    style={{
                      fontWeight: 500,
                      color: '#525f7f',
                      fontSize: '0.875rem',
                      marginBottom: '0.25rem',
                    }}
                  >
                    New Expiration
                  </Label>
                  <Input
                    type="text"
                    name="updateNewExpiration"
                    id="updateNewExpiration"
                    placeholder="YYYY-MM-DD"
                    value={updateNewExpiration}
                    onChange={(e) => setUpdateNewExpiration(e.target.value)}
                    style={{
                      border: '1px solid #cad1d7',
                      borderRadius: '0.375rem',
                      fontSize: '0.875rem',
                      padding: '0.5rem',
                    }}
                    bsSize="sm"
                  />
                </FormGroup>
              </Col>
            </Row>
            <div
              style={{
                display: 'flex',
                justifyContent: 'flex-end',
                paddingTop: '0.75rem',
                borderTop: '1px solid #e9ecef',
                marginTop: '0.5rem',
              }}
            >
              <Button
                color="primary"
                onClick={handleUpdate}
                size="sm"
                style={{
                  padding: '0.5rem 1.5rem',
                  fontSize: '0.875rem',
                  fontWeight: 600,
                  borderRadius: '0.375rem',
                }}
              >
                Update Subscription
              </Button>
            </div>
          </div>
        </Card>
      )}

      {/* Seat Increase Form */}
      {operation === 'seatIncrease' && (
        <Card
          className="shadow-sm"
          style={{ border: '1px solid #e9ecef', marginBottom: '1rem' }}
        >
          <CardHeader
            style={{
              backgroundColor: '#d1ecf1',
              borderBottom: '1px solid #e9ecef',
              padding: '0.5rem 1rem',
            }}
          >
            <h6
              style={{
                margin: 0,
                color: '#0c5460',
                fontWeight: 600,
                fontSize: '0.875rem',
              }}
            >
              📈 {operationLabels.seatIncrease}
            </h6>
          </CardHeader>
          <div style={{ padding: '1rem' }}>
            <Row>
              <Col md={6}>
                <FormGroup style={{ marginBottom: '0.75rem' }}>
                  <Label
                    for="seatIncreaseResellerName"
                    style={{
                      fontWeight: 500,
                      color: '#525f7f',
                      fontSize: '0.875rem',
                      marginBottom: '0.25rem',
                    }}
                  >
                    Reseller Name <span style={{ color: '#e74c3c' }}>*</span>
                  </Label>
                  <Input
                    type="select"
                    name="seatIncreaseResellerName"
                    id="seatIncreaseResellerName"
                    value={seatIncreaseResellerName}
                    onChange={(e) =>
                      setSeatIncreaseResellerName(e.target.value)
                    }
                    style={{
                      border: '1px solid #cad1d7',
                      borderRadius: '0.375rem',
                      fontSize: '0.875rem',
                      padding: '0.5rem',
                    }}
                    bsSize="sm"
                  >
                    <option value="WebPT">WebPT</option>
                    <option value="Stride">Stride</option>
                    <option value="Empower">Empower</option>
                    <option value="Other">Other</option>
                  </Input>
                  {seatIncreaseResellerName === 'Other' && (
                    <Input
                      type="text"
                      name="seatIncreaseResellerNameOther"
                      id="seatIncreaseResellerNameOther"
                      placeholder="Enter reseller name"
                      value={seatIncreaseResellerNameOther}
                      onChange={(e) =>
                        setSeatIncreaseResellerNameOther(e.target.value)
                      }
                      style={{
                        border: '1px solid #cad1d7',
                        borderRadius: '0.375rem',
                        fontSize: '0.875rem',
                        padding: '0.5rem',
                        marginTop: '0.5rem',
                      }}
                      bsSize="sm"
                    />
                  )}
                </FormGroup>
                <FormGroup style={{ marginBottom: '0.75rem' }}>
                  <Label
                    for="seatIncreaseGroupSubscriptionId"
                    style={{
                      fontWeight: 500,
                      color: '#525f7f',
                      fontSize: '0.875rem',
                      marginBottom: '0.25rem',
                    }}
                  >
                    Group Subscription ID{' '}
                    <span style={{ color: '#e74c3c' }}>*</span>
                  </Label>
                  <Input
                    type="number"
                    name="seatIncreaseGroupSubscriptionId"
                    id="seatIncreaseGroupSubscriptionId"
                    placeholder="123456"
                    value={seatIncreaseGroupSubscriptionId}
                    onChange={(e) =>
                      setSeatIncreaseGroupSubscriptionId(e.target.value)
                    }
                    style={{
                      border: '1px solid #cad1d7',
                      borderRadius: '0.375rem',
                      fontSize: '0.875rem',
                      padding: '0.5rem',
                    }}
                    bsSize="sm"
                  />
                </FormGroup>
                <FormGroup style={{ marginBottom: '0.75rem' }}>
                  <Label
                    for="seatIncreaseNewSeatCount"
                    style={{
                      fontWeight: 500,
                      color: '#525f7f',
                      fontSize: '0.875rem',
                      marginBottom: '0.25rem',
                    }}
                  >
                    New Seat Count <span style={{ color: '#e74c3c' }}>*</span>
                  </Label>
                  <Input
                    type="number"
                    name="seatIncreaseNewSeatCount"
                    id="seatIncreaseNewSeatCount"
                    placeholder="15"
                    value={seatIncreaseNewSeatCount}
                    onChange={(e) =>
                      setSeatIncreaseNewSeatCount(e.target.value)
                    }
                    style={{
                      border: '1px solid #cad1d7',
                      borderRadius: '0.375rem',
                      fontSize: '0.875rem',
                      padding: '0.5rem',
                    }}
                    bsSize="sm"
                  />
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup style={{ marginBottom: '0.75rem' }}>
                  <Label
                    for="seatIncreaseSubscriberEmail"
                    style={{
                      fontWeight: 500,
                      color: '#525f7f',
                      fontSize: '0.875rem',
                      marginBottom: '0.25rem',
                    }}
                  >
                    Subscriber Email <span style={{ color: '#e74c3c' }}>*</span>
                  </Label>
                  <Input
                    type="email"
                    name="seatIncreaseSubscriberEmail"
                    id="seatIncreaseSubscriberEmail"
                    placeholder="subscriber@example.com"
                    value={seatIncreaseSubscriberEmail}
                    onChange={(e) =>
                      setSeatIncreaseSubscriberEmail(e.target.value)
                    }
                    style={{
                      border: '1px solid #cad1d7',
                      borderRadius: '0.375rem',
                      fontSize: '0.875rem',
                      padding: '0.5rem',
                    }}
                    bsSize="sm"
                  />
                </FormGroup>
                <FormGroup style={{ marginBottom: '0.75rem' }}>
                  <Label
                    for="seatIncreaseOldSeatCount"
                    style={{
                      fontWeight: 500,
                      color: '#525f7f',
                      fontSize: '0.875rem',
                      marginBottom: '0.25rem',
                    }}
                  >
                    Old Seat Count
                  </Label>
                  <Input
                    type="number"
                    name="seatIncreaseOldSeatCount"
                    id="seatIncreaseOldSeatCount"
                    placeholder="10"
                    value={seatIncreaseOldSeatCount}
                    onChange={(e) =>
                      setSeatIncreaseOldSeatCount(e.target.value)
                    }
                    style={{
                      border: '1px solid #cad1d7',
                      borderRadius: '0.375rem',
                      fontSize: '0.875rem',
                      padding: '0.5rem',
                    }}
                    bsSize="sm"
                  />
                </FormGroup>
                <FormGroup style={{ marginBottom: '0.75rem' }}>
                  <Label
                    for="seatIncreaseEventDate"
                    style={{
                      fontWeight: 500,
                      color: '#525f7f',
                      fontSize: '0.875rem',
                      marginBottom: '0.25rem',
                    }}
                  >
                    Event Date
                  </Label>
                  <DatePicker
                    selected={seatIncreaseEventDate}
                    onChange={(date) => setSeatIncreaseEventDate(date)}
                    dateFormat="yyyy-MM-dd"
                    className="form-control form-control-sm"
                    style={{
                      border: '1px solid #cad1d7',
                      borderRadius: '0.375rem',
                      padding: '0.375rem',
                      fontSize: '0.875rem',
                    }}
                  />
                </FormGroup>
              </Col>
            </Row>
            <div
              style={{
                display: 'flex',
                justifyContent: 'flex-end',
                paddingTop: '0.75rem',
                borderTop: '1px solid #e9ecef',
                marginTop: '0.5rem',
              }}
            >
              <Button
                color="primary"
                onClick={handleSeatIncrease}
                size="sm"
                style={{
                  padding: '0.5rem 1.5rem',
                  fontSize: '0.875rem',
                  fontWeight: 600,
                  borderRadius: '0.375rem',
                }}
              >
                Process Seat Increase
              </Button>
            </div>
          </div>
        </Card>
      )}

      {/* Cancel Subscription Form */}
      {operation === 'cancel' && (
        <Card
          className="shadow-sm"
          style={{ border: '1px solid #e9ecef', marginBottom: '1rem' }}
        >
          <CardHeader
            style={{
              backgroundColor: '#f8d7da',
              borderBottom: '1px solid #e9ecef',
              padding: '0.5rem 1rem',
            }}
          >
            <h6
              style={{
                margin: 0,
                color: '#721c24',
                fontWeight: 600,
                fontSize: '0.875rem',
              }}
            >
              ❌ {operationLabels.cancel}
            </h6>
          </CardHeader>
          <div style={{ padding: '1rem' }}>
            <div
              style={{
                backgroundColor: '#d1ecf1',
                border: '1px solid #bee5eb',
                borderRadius: '0.375rem',
                padding: '0.75rem',
                marginBottom: '1rem',
                fontSize: '0.875rem',
              }}
            >
              <strong style={{ color: '#0c5460' }}>ℹ️ Info:</strong>
              <span style={{ color: '#0c5460', marginLeft: '0.5rem' }}>
                This creates a cancellation event in ChartMogul but does NOT
                delete records. Use "Delete Subscription Records" to permanently
                remove data.
              </span>
            </div>
            <Row>
              <Col md={6}>
                <FormGroup style={{ marginBottom: '0.75rem' }}>
                  <Label
                    for="cancelResellerName"
                    style={{
                      fontWeight: 500,
                      color: '#525f7f',
                      fontSize: '0.875rem',
                      marginBottom: '0.25rem',
                    }}
                  >
                    Reseller Name <span style={{ color: '#e74c3c' }}>*</span>
                  </Label>
                  <Input
                    type="select"
                    name="cancelResellerName"
                    id="cancelResellerName"
                    value={cancelResellerName}
                    onChange={(e) => setCancelResellerName(e.target.value)}
                    style={{
                      border: '1px solid #cad1d7',
                      borderRadius: '0.375rem',
                      fontSize: '0.875rem',
                      padding: '0.5rem',
                    }}
                    bsSize="sm"
                  >
                    <option value="WebPT">WebPT</option>
                    <option value="Stride">Stride</option>
                    <option value="Empower">Empower</option>
                    <option value="Other">Other</option>
                  </Input>
                  {cancelResellerName === 'Other' && (
                    <Input
                      type="text"
                      name="cancelResellerNameOther"
                      id="cancelResellerNameOther"
                      placeholder="Enter reseller name"
                      value={cancelResellerNameOther}
                      onChange={(e) =>
                        setCancelResellerNameOther(e.target.value)
                      }
                      style={{
                        border: '1px solid #cad1d7',
                        borderRadius: '0.375rem',
                        fontSize: '0.875rem',
                        padding: '0.5rem',
                        marginTop: '0.5rem',
                      }}
                      bsSize="sm"
                    />
                  )}
                </FormGroup>
                <FormGroup style={{ marginBottom: '0.75rem' }}>
                  <Label
                    for="cancelGroupSubscriptionId"
                    style={{
                      fontWeight: 500,
                      color: '#525f7f',
                      fontSize: '0.875rem',
                      marginBottom: '0.25rem',
                    }}
                  >
                    Group Subscription ID{' '}
                    <span style={{ color: '#e74c3c' }}>*</span>
                  </Label>
                  <Input
                    type="number"
                    name="cancelGroupSubscriptionId"
                    id="cancelGroupSubscriptionId"
                    placeholder="123456"
                    value={cancelGroupSubscriptionId}
                    onChange={(e) =>
                      setCancelGroupSubscriptionId(e.target.value)
                    }
                    style={{
                      border: '1px solid #cad1d7',
                      borderRadius: '0.375rem',
                      fontSize: '0.875rem',
                      padding: '0.5rem',
                    }}
                    bsSize="sm"
                  />
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup style={{ marginBottom: '0.75rem' }}>
                  <Label
                    for="cancelSubscriberEmail"
                    style={{
                      fontWeight: 500,
                      color: '#525f7f',
                      fontSize: '0.875rem',
                      marginBottom: '0.25rem',
                    }}
                  >
                    Subscriber Email <span style={{ color: '#e74c3c' }}>*</span>
                  </Label>
                  <Input
                    type="email"
                    name="cancelSubscriberEmail"
                    id="cancelSubscriberEmail"
                    placeholder="subscriber@example.com"
                    value={cancelSubscriberEmail}
                    onChange={(e) => setCancelSubscriberEmail(e.target.value)}
                    style={{
                      border: '1px solid #cad1d7',
                      borderRadius: '0.375rem',
                      fontSize: '0.875rem',
                      padding: '0.5rem',
                    }}
                    bsSize="sm"
                  />
                </FormGroup>
                <FormGroup style={{ marginBottom: '0.75rem' }}>
                  <Label
                    for="cancelEventDate"
                    style={{
                      fontWeight: 500,
                      color: '#525f7f',
                      fontSize: '0.875rem',
                      marginBottom: '0.25rem',
                    }}
                  >
                    Event Date
                  </Label>
                  <DatePicker
                    selected={cancelEventDate}
                    onChange={(date) => setCancelEventDate(date)}
                    dateFormat="yyyy-MM-dd"
                    className="form-control form-control-sm"
                    style={{
                      border: '1px solid #cad1d7',
                      borderRadius: '0.375rem',
                      padding: '0.375rem',
                      fontSize: '0.875rem',
                    }}
                  />
                </FormGroup>
              </Col>
            </Row>
            <div
              style={{
                display: 'flex',
                justifyContent: 'flex-end',
                paddingTop: '0.75rem',
                borderTop: '1px solid #e9ecef',
                marginTop: '0.5rem',
              }}
            >
              <Button
                color="warning"
                onClick={handleCancel}
                size="sm"
                style={{
                  padding: '0.5rem 1.5rem',
                  fontSize: '0.875rem',
                  fontWeight: 600,
                  borderRadius: '0.375rem',
                }}
              >
                Cancel Subscription
              </Button>
            </div>
          </div>
        </Card>
      )}

      {/* Delete Form */}
      {operation === 'delete' && (
        <Card
          className="shadow-sm"
          style={{ border: '1px solid #e9ecef', marginBottom: '1rem' }}
        >
          <CardHeader
            style={{
              backgroundColor: '#f8d7da',
              borderBottom: '1px solid #e9ecef',
              padding: '0.5rem 1rem',
            }}
          >
            <h6
              style={{
                margin: 0,
                color: '#721c24',
                fontWeight: 600,
                fontSize: '0.875rem',
              }}
            >
              🗑️ {operationLabels.delete}
            </h6>
          </CardHeader>
          <div style={{ padding: '1rem' }}>
            <div
              style={{
                backgroundColor: '#fff3cd',
                border: '1px solid #ffc107',
                borderRadius: '0.375rem',
                padding: '0.75rem',
                marginBottom: '1rem',
                fontSize: '0.875rem',
              }}
            >
              <strong style={{ color: '#856404' }}>⚠️ Warning:</strong>
              <span style={{ color: '#856404', marginLeft: '0.5rem' }}>
                This will permanently DELETE ChartMogul subscription records
                including invoices and events. Use "Cancel Subscription" to
                create a cancellation event without deleting records.
              </span>
            </div>
            <Row>
              <Col md={6}>
                <FormGroup style={{ marginBottom: '0.75rem' }}>
                  <Label
                    for="deleteSubscriptionId"
                    style={{
                      fontWeight: 500,
                      color: '#525f7f',
                      fontSize: '0.875rem',
                      marginBottom: '0.25rem',
                    }}
                  >
                    Subscription ID <span style={{ color: '#e74c3c' }}>*</span>
                  </Label>
                  <Input
                    type="number"
                    name="deleteSubscriptionId"
                    id="deleteSubscriptionId"
                    placeholder="123456"
                    value={deleteSubscriptionId}
                    onChange={(e) => setDeleteSubscriptionId(e.target.value)}
                    style={{
                      border: '1px solid #cad1d7',
                      borderRadius: '0.375rem',
                      fontSize: '0.875rem',
                      padding: '0.5rem',
                    }}
                    bsSize="sm"
                  />
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup style={{ marginBottom: '0.75rem' }}>
                  <Label
                    for="deleteSubscriberEmail"
                    style={{
                      fontWeight: 500,
                      color: '#525f7f',
                      fontSize: '0.875rem',
                      marginBottom: '0.25rem',
                    }}
                  >
                    Subscriber Email
                  </Label>
                  <Input
                    type="email"
                    name="deleteSubscriberEmail"
                    id="deleteSubscriberEmail"
                    placeholder="subscriber@example.com"
                    value={deleteSubscriberEmail}
                    onChange={(e) => setDeleteSubscriberEmail(e.target.value)}
                    style={{
                      border: '1px solid #cad1d7',
                      borderRadius: '0.375rem',
                      fontSize: '0.875rem',
                      padding: '0.5rem',
                    }}
                    bsSize="sm"
                  />
                </FormGroup>
              </Col>
            </Row>
            <div
              style={{
                display: 'flex',
                justifyContent: 'flex-end',
                paddingTop: '0.75rem',
                borderTop: '1px solid #e9ecef',
                marginTop: '0.5rem',
              }}
            >
              <Button
                color="danger"
                onClick={handleDelete}
                size="sm"
                style={{
                  padding: '0.5rem 1.5rem',
                  fontSize: '0.875rem',
                  fontWeight: 600,
                  borderRadius: '0.375rem',
                }}
              >
                Delete Subscription
              </Button>
            </div>
          </div>
        </Card>
      )}

      {/* Issue Invoices Form */}
      {operation === 'issueInvoices' && (
        <Card
          className="shadow-sm"
          style={{ border: '1px solid #e9ecef', marginBottom: '1rem' }}
        >
          <CardHeader
            style={{
              backgroundColor: '#d4edda',
              borderBottom: '1px solid #e9ecef',
              padding: '0.5rem 1rem',
            }}
          >
            <h6
              style={{
                margin: 0,
                color: '#155724',
                fontWeight: 600,
                fontSize: '0.875rem',
              }}
            >
              💰 {operationLabels.issueInvoices}
            </h6>
          </CardHeader>
          <div style={{ padding: '1rem' }}>
            <div
              style={{
                backgroundColor: '#d1ecf1',
                border: '1px solid #bee5eb',
                borderRadius: '0.375rem',
                padding: '0.75rem',
                marginBottom: '1rem',
                fontSize: '0.875rem',
              }}
            >
              <strong style={{ color: '#0c5460' }}>ℹ️ Info:</strong>
              <span style={{ color: '#0c5460', marginLeft: '0.5rem' }}>
                This will process all active subscriptions and issue monthly
                invoices for those that are due.
              </span>
            </div>
            <Row>
              <Col md={6}>
                <FormGroup style={{ marginBottom: '0.75rem' }}>
                  <Label
                    for="issueInvoicesEventDate"
                    style={{
                      fontWeight: 500,
                      color: '#525f7f',
                      fontSize: '0.875rem',
                      marginBottom: '0.25rem',
                    }}
                  >
                    Event Date
                  </Label>
                  <DatePicker
                    selected={issueInvoicesEventDate}
                    onChange={(date) => setIssueInvoicesEventDate(date)}
                    dateFormat="yyyy-MM-dd"
                    className="form-control form-control-sm"
                    style={{
                      border: '1px solid #cad1d7',
                      borderRadius: '0.375rem',
                      padding: '0.375rem',
                      fontSize: '0.875rem',
                    }}
                  />
                </FormGroup>
              </Col>
            </Row>
            <div
              style={{
                display: 'flex',
                justifyContent: 'flex-end',
                paddingTop: '0.75rem',
                borderTop: '1px solid #e9ecef',
                marginTop: '0.5rem',
              }}
            >
              <Button
                color="success"
                onClick={handleIssueInvoices}
                size="sm"
                style={{
                  padding: '0.5rem 1.5rem',
                  fontSize: '0.875rem',
                  fontWeight: 600,
                  borderRadius: '0.375rem',
                }}
              >
                Issue Due Invoices
              </Button>
            </div>
          </div>
        </Card>
      )}

      {/* Response Display */}
      <Card
        className="shadow-sm"
        style={{ border: '1px solid #e9ecef', marginTop: '1rem' }}
      >
        <CardHeader
          style={{
            backgroundColor: '#f8f9fa',
            borderBottom: '1px solid #e9ecef',
            padding: '0.5rem 1rem',
          }}
        >
          <h6
            style={{
              margin: 0,
              color: '#525f7f',
              fontWeight: 600,
              fontSize: '0.875rem',
            }}
          >
            Response
          </h6>
        </CardHeader>
        <div style={{ padding: '1rem', backgroundColor: '#f8f9fa' }}>
          <pre
            style={{
              margin: 0,
              padding: '0.75rem',
              backgroundColor: '#ffffff',
              border: '1px solid #e9ecef',
              borderRadius: '0.375rem',
              fontSize: '0.8rem',
              overflow: 'auto',
              maxHeight: '300px',
              fontFamily:
                'Monaco, Menlo, "Ubuntu Mono", Consolas, "source-code-pro", monospace',
            }}
          >
            {JSON.stringify(rsp, null, 2)}
          </pre>
        </div>
      </Card>
    </>
  );
};

// ─── Transcription Fleet Dashboard ───────────────────────────────────────────

const fmt = (n, decimals = 1) =>
  n == null ? '—' : Number(n).toFixed(decimals);

const fmtUptime = (s) => {
  if (s == null) return '—';
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  return h > 0 ? `${h}h ${m}m` : m > 0 ? `${m}m ${sec}s` : `${sec}s`;
};

const workerStatusBadge = (w) => {
  // LOST = DDB says ready but ASG no longer has the instance — the
  // worker died without running its cleanup. Different from STALE (worker
  // is alive but heartbeat is broken) and TERMINATED (graceful shutdown).
  // BE sets `presumed_lost: true` on these rows; we read that here.
  if (w.presumed_lost)
    return (
      <span
        style={{
          color: '#fff',
          background: '#6f42c1',
          padding: '2px 7px',
          borderRadius: 4,
          fontSize: '0.8rem',
        }}
        title="Heartbeat went stale and the ASG no longer has this instance. Worker died without cleanup."
      >
        LOST
      </span>
    );
  // Terminated rows: graceful shutdown ran. DDB TTL prunes them 24h after.
  if (w.status === 'terminated')
    return (
      <span
        style={{
          color: '#fff',
          background: '#6c757d',
          padding: '2px 7px',
          borderRadius: 4,
          fontSize: '0.8rem',
        }}
      >
        TERMINATED
      </span>
    );
  const isStale = w.last_heartbeat_age_s != null && w.last_heartbeat_age_s > 90;
  if (isStale || w.status === 'stale')
    return (
      <span
        style={{
          color: '#fff',
          background: '#dc3545',
          padding: '2px 7px',
          borderRadius: 4,
          fontSize: '0.8rem',
        }}
        title="Heartbeat is stale but the ASG still has this instance — worker is alive, heartbeat thread is wedged."
      >
        STALE
      </span>
    );
  if (w.status === 'draining')
    return (
      <span
        style={{
          color: '#fff',
          background: '#fd7e14',
          padding: '2px 7px',
          borderRadius: 4,
          fontSize: '0.8rem',
        }}
      >
        DRAINING
      </span>
    );
  return (
    <span
      style={{
        color: '#fff',
        background: '#28a745',
        padding: '2px 7px',
        borderRadius: 4,
        fontSize: '0.8rem',
      }}
    >
      READY
    </span>
  );
};

const queueColor = (visible, dlq) => {
  if (dlq > 0 || visible > 30) return '#dc3545';
  if (visible > 5) return '#ffc107';
  return '#28a745';
};

const EXCLUDED_WORKERS_KEY = 'transcription_fleet_excluded_workers';

const loadExcluded = () => {
  try {
    return new Set(
      JSON.parse(sessionStorage.getItem(EXCLUDED_WORKERS_KEY) || '[]'),
    );
  } catch {
    return new Set();
  }
};

const saveExcluded = (set) => {
  sessionStorage.setItem(EXCLUDED_WORKERS_KEY, JSON.stringify([...set]));
};

// Per-source palette used by traffic-split + latency mini-charts.
// Self-hosted intentionally green (the cost-win path).
const SOURCE_COLORS = {
  'self-hosted': '#28a745',
  groq: '#5e72e4',
  openai: '#fd7e14',
};

// us-east-1 on-demand $/hr — mirrors Transcription_TL/Transcription/Support/InstancePricing.py.
// Update when AWS pricing moves (cents/year scale).
const INSTANCE_HOURLY_COST = {
  'g6.xlarge': 0.8048,
  'g6.2xlarge': 0.9776,
  'g6.4xlarge': 1.3232,
  'g6.8xlarge': 2.0144,
  'g5.xlarge': 1.006,
  'g5.2xlarge': 1.212,
  'g5.4xlarge': 1.624,
  'g4dn.xlarge': 0.526,
  'g4dn.2xlarge': 0.752,
};

// Stacked horizontal bar showing what fraction of recent traffic went to
// each provider. Zero-state renders a flat gray strip.
const TrafficSplitBar = ({ traffic }) => {
  if (!traffic || !traffic.total) {
    return (
      <div
        style={{
          height: 6,
          background: '#e9ecef',
          borderRadius: 3,
          marginTop: 10,
        }}
      />
    );
  }
  return (
    <div
      style={{
        display: 'flex',
        height: 6,
        borderRadius: 3,
        overflow: 'hidden',
        marginTop: 10,
      }}
    >
      {Object.entries(traffic.by_source).map(([source, data]) => (
        <div
          key={source}
          style={{
            background: SOURCE_COLORS[source] || '#6c757d',
            width: `${(data.pct ?? 0) * 100}%`,
            minWidth: data.pct > 0 ? 2 : 0,
          }}
          title={`${source}: ${Math.round((data.pct ?? 0) * 100)}% (${data.count} tx)`}
        />
      ))}
    </div>
  );
};

// One row per source, relative-length bar + numeric value. Lets the
// operator compare provider latencies at a glance.
const LatencyByProviderBars = ({ traffic }) => {
  if (!traffic) return null;
  const entries = Object.entries(traffic.by_source).filter(
    ([, d]) => d.avg_latency_s != null,
  );
  if (entries.length === 0) {
    return (
      <div style={{ marginTop: 10, fontSize: '0.72rem', color: '#6c757d' }}>
        no traffic in window
      </div>
    );
  }
  const max = Math.max(...entries.map(([, d]) => d.avg_latency_s));
  return (
    <div style={{ marginTop: 10 }}>
      {entries.map(([source, data]) => (
        <div
          key={source}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            marginTop: 2,
          }}
        >
          <span style={{ fontSize: '0.7rem', width: 56, color: '#6c757d' }}>
            {source}
          </span>
          <div
            style={{
              flex: 1,
              height: 4,
              background: '#e9ecef',
              borderRadius: 2,
            }}
          >
            <div
              style={{
                width: `${max > 0 ? (data.avg_latency_s / max) * 100 : 0}%`,
                height: '100%',
                background: SOURCE_COLORS[source] || '#6c757d',
                borderRadius: 2,
              }}
            />
          </div>
          <span
            style={{
              fontSize: '0.72rem',
              color: '#495057',
              width: 38,
              textAlign: 'right',
            }}
          >
            {data.avg_latency_s.toFixed(1)}s
          </span>
        </div>
      ))}
    </div>
  );
};

// ─────────────────────────── Worker history chart ────────────────────────────
// Workers vanish from the live status snapshot the moment they terminate
// (DDB row TTLs out). This component reads CloudWatch via /transcription/history
// so we still have an audit trail of "what the fleet looked like at 9am" even
// if everything has since scaled back to zero.
//
// Backed by AWS/AutoScaling.GroupInServiceInstances (truth) plus our own
// TranscriptionLatencySeconds SampleCount (jobs served per bucket).
const HISTORY_RANGES = [
  { label: '1h', lookback: 60, period: 60 },
  { label: '6h', lookback: 6 * 60, period: 60 },
  { label: '24h', lookback: 24 * 60, period: 300 },
  { label: '7d', lookback: 7 * 24 * 60, period: 1800 },
];

const formatTimeLabel = (iso, lookbackMin) => {
  const d = new Date(iso);
  if (lookbackMin <= 6 * 60) {
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }
  if (lookbackMin <= 24 * 60) {
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }
  return d.toLocaleString([], {
    month: 'numeric',
    day: 'numeric',
    hour: '2-digit',
  });
};

const WorkerHistoryChart = ({ apiClient }) => {
  const [rangeIdx, setRangeIdx] = useState(2); // default 24h
  const [history, setHistory] = useState(null);
  const [historyError, setHistoryError] = useState(null);
  const [loading, setLoading] = useState(false);

  const range = HISTORY_RANGES[rangeIdx];

  const refetch = () => {
    setLoading(true);
    apiClient
      .transcriptionHistory(range.lookback, range.period)
      .then((data) => {
        setHistory(data);
        setHistoryError(null);
      })
      .catch((err) => setHistoryError(String(err)))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    refetch();
    // Refresh every 60s — CloudWatch publishes ASG metrics on a 1-min cadence
    // so anything faster is wasted polling.
    const id = setInterval(refetch, 60_000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rangeIdx]);

  const buckets = history?.buckets ?? [];
  const labels = buckets.map((b) => formatTimeLabel(b.ts, range.lookback));

  // Active = max of (in_service, desired) so an empty no-data bucket still
  // shows on the chart when we *wanted* workers but none came up.
  //
  // workers_in_service comes from CloudWatch's Average stat over the
  // bucket, which can be fractional (a fleet that's 4 for part of the
  // bucket and 5 for the rest averages to ~4.8). Mathematically real,
  // but visually confusing on a chart that's meant to show step changes
  // in integer instance count. Round at the plot layer so the line
  // looks like the step function ops thinks of it as. The fractional
  // value is preserved in the BE-computed worker-hours rollup below
  // (costTotals.self_hosted_worker_hours), so $$/cost math stays exact.
  const inService = buckets.map((b) =>
    b.workers_in_service != null ? Math.round(b.workers_in_service) : null,
  );
  const desired = buckets.map((b) =>
    b.workers_desired != null ? b.workers_desired : null,
  );

  // Summary stats across the selected window (the panel header)
  const totalJobs = buckets.reduce((s, b) => s + (b.self_hosted_jobs || 0), 0);
  const latencySamples = buckets.filter(
    (b) => b.self_hosted_avg_latency_s != null && b.self_hosted_jobs > 0,
  );
  const avgLatency =
    latencySamples.length > 0
      ? latencySamples.reduce(
          (s, b) => s + b.self_hosted_avg_latency_s * b.self_hosted_jobs,
          0,
        ) / latencySamples.reduce((s, b) => s + b.self_hosted_jobs, 0)
      : null;
  const peakWorkers = inService.reduce(
    (m, v) => (v != null && v > m ? v : m),
    0,
  );
  // Cost rollup straight from the BE — already folds the self-hosted infra
  // cost (worker-hours × $/hr) in alongside per-call provider spend.
  const costTotals = history?.totals ?? {};
  const costBySource = costTotals.cost_by_source_usd || {};
  const totalCostUsd = costTotals.total_cost_usd ?? 0;

  const workerHoursLive = (() => {
    // Prefer the BE-computed integral. Fall back to a local sum (legacy
    // payloads, or if totals.* aren't populated yet).
    if (costTotals.self_hosted_worker_hours != null) {
      return costTotals.self_hosted_worker_hours;
    }
    // ∫ workers dt ≈ Σ (avg_workers_in_bucket * period_seconds) / 3600
    const periodH = (history?.period_seconds ?? range.period) / 3600;
    return buckets.reduce(
      (s, b) =>
        s + (b.workers_in_service != null ? b.workers_in_service : 0) * periodH,
      0,
    );
  })();

  const chartData = {
    labels,
    datasets: [
      {
        label: 'In service',
        data: inService,
        borderColor: '#28a745',
        backgroundColor: 'rgba(40, 167, 69, 0.18)',
        borderWidth: 2,
        steppedLine: 'after', // workers come up / down in discrete steps
        pointRadius: 0,
        pointHoverRadius: 4,
        fill: true,
        spanGaps: true,
      },
      {
        label: 'Desired',
        data: desired,
        borderColor: '#6c757d',
        backgroundColor: 'transparent',
        borderWidth: 1.5,
        borderDash: [4, 4],
        steppedLine: 'after',
        pointRadius: 0,
        pointHoverRadius: 3,
        fill: false,
        spanGaps: true,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    legend: {
      display: true,
      position: 'top',
      align: 'end',
      labels: { boxWidth: 12, fontSize: 11 },
    },
    tooltips: {
      mode: 'index',
      intersect: false,
      callbacks: {
        title: (items) => (items.length ? items[0].xLabel : ''),
        // We render every per-bucket stat in one tooltip — that's the whole
        // point of the hover ask. Order: workers, jobs, latency.
        label: (item, data) => {
          const b = buckets[item.index] || {};
          const ds = data.datasets[item.datasetIndex];
          // Suppress the second dataset's label when "in service" line is
          // showing — we'll dump everything via afterBody instead.
          if (ds.label === 'Desired') return null;
          const lines = [
            // workers_in_service comes from CloudWatch's Average stat over
            // the bucket, so the raw value can be fractional (e.g. "4.8"
            // when the fleet was at 4 for part of the bucket and 5 for
            // the rest). Instance counts are conceptually integers though,
            // so round for display. Matches the .toFixed(0) on Desired
            // below — same intent, different upstream stat.
            `In service: ${b.workers_in_service != null ? Math.round(b.workers_in_service) : '—'}`,
            `Desired:    ${b.workers_desired != null ? b.workers_desired.toFixed(0) : '—'}`,
            `Jobs:       ${b.self_hosted_jobs}`,
            `Avg latency: ${b.self_hosted_avg_latency_s != null ? b.self_hosted_avg_latency_s.toFixed(2) + 's' : '—'}`,
          ];
          return lines;
        },
      },
    },
    hover: { mode: 'index', intersect: false },
    scales: {
      xAxes: [
        {
          ticks: {
            maxRotation: 0,
            autoSkip: true,
            maxTicksLimit: 8,
            fontSize: 10,
            fontColor: '#6c757d',
          },
          gridLines: { display: false },
        },
      ],
      yAxes: [
        {
          ticks: {
            beginAtZero: true,
            precision: 0,
            fontSize: 10,
            fontColor: '#6c757d',
            // step in whole workers
            callback: (value) => (Number.isInteger(value) ? value : ''),
          },
          gridLines: { color: '#f1f3f5' },
          scaleLabel: {
            display: true,
            labelString: 'Workers',
            fontSize: 11,
            fontColor: '#6c757d',
          },
        },
      ],
    },
  };

  const headerStat = {
    fontSize: '0.72rem',
    color: '#6c757d',
    textTransform: 'uppercase',
  };
  const headerVal = {
    fontSize: '1.1rem',
    fontWeight: 700,
    color: '#212529',
    marginTop: 2,
  };

  return (
    <div
      style={{
        background: '#fff',
        border: '1px solid #e3e6ea',
        borderRadius: 10,
        padding: '14px 18px',
        marginBottom: 18,
        boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 14,
          marginBottom: 10,
        }}
      >
        <strong style={{ fontSize: '0.95rem' }}>Worker history</strong>
        <div style={{ display: 'flex', gap: 4 }}>
          {HISTORY_RANGES.map((r, i) => (
            <Button
              key={r.label}
              size="sm"
              color={i === rangeIdx ? 'primary' : 'light'}
              outline={i !== rangeIdx}
              style={{ padding: '2px 10px', fontSize: '0.75rem' }}
              onClick={() => setRangeIdx(i)}
            >
              {r.label}
            </Button>
          ))}
        </div>
        <div
          style={{
            marginLeft: 'auto',
            display: 'flex',
            gap: 16,
            alignItems: 'flex-start',
          }}
        >
          <div>
            <div style={headerStat}>Peak workers</div>
            <div style={headerVal}>
              {peakWorkers > 0 ? peakWorkers.toFixed(0) : '—'}
            </div>
          </div>
          <div>
            <div style={headerStat}>Worker-hrs</div>
            <div style={headerVal}>
              {workerHoursLive > 0 ? workerHoursLive.toFixed(1) : '—'}
            </div>
          </div>
          <div>
            <div style={headerStat}>Self-hosted jobs</div>
            <div style={headerVal}>{totalJobs}</div>
          </div>
          <div>
            <div style={headerStat}>Avg latency</div>
            <div style={headerVal}>
              {avgLatency != null ? `${avgLatency.toFixed(2)}s` : '—'}
            </div>
          </div>
          {/* Window cost — sums per-call provider spend + integrated
              self-hosted infra cost. Hover for the source breakdown so we
              don't blow up the header strip on the default view. */}
          <div
            title={
              Object.keys(costBySource).length
                ? Object.entries(costBySource)
                    .map(([s, v]) => `${s}: $${(v ?? 0).toFixed(4)}`)
                    .join('\n')
                : 'No cost data in this window.'
            }
            style={{
              borderLeft: '1px solid #e3e6ea',
              paddingLeft: 14,
              cursor: 'help',
            }}
          >
            <div style={headerStat}>
              {range.label} cost{' '}
              <span style={{ color: '#adb5bd', fontWeight: 400 }}>ⓘ</span>
            </div>
            <div style={headerVal}>
              {totalCostUsd > 0 ? `$${totalCostUsd.toFixed(2)}` : '—'}
            </div>
            {/* Tiny per-source row below the headline number — three small
                pills so the breakdown is glanceable without hovering. */}
            <div style={{ display: 'flex', gap: 6, marginTop: 4 }}>
              {['self-hosted', 'groq', 'openai'].map((src) => {
                const v = costBySource[src];
                if (v == null || v === 0) return null;
                return (
                  <span
                    key={src}
                    style={{
                      fontSize: '0.65rem',
                      padding: '1px 6px',
                      borderRadius: 8,
                      background: '#f1f3f5',
                      color: '#495057',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    <span
                      style={{
                        display: 'inline-block',
                        width: 6,
                        height: 6,
                        borderRadius: 3,
                        background: SOURCE_COLORS[src] || '#6c757d',
                        marginRight: 4,
                        verticalAlign: 'middle',
                      }}
                    />
                    {src.replace('-', '·')} ${v.toFixed(2)}
                  </span>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {historyError && (
        <div
          style={{
            background: '#fff3cd',
            color: '#856404',
            padding: '8px 12px',
            borderRadius: 4,
            fontSize: '0.8rem',
            marginBottom: 8,
          }}
        >
          History fetch error: {historyError}
        </div>
      )}

      {history == null && !historyError && (
        <div
          style={{
            color: '#6c757d',
            padding: '40px 0',
            textAlign: 'center',
            fontSize: '0.85rem',
          }}
        >
          {loading ? 'Loading…' : 'No data yet.'}
        </div>
      )}

      {history != null && (
        <div style={{ height: 220 }}>
          <Line data={chartData} options={chartOptions} />
        </div>
      )}

      <div style={{ marginTop: 6, fontSize: '0.7rem', color: '#6c757d' }}>
        {history && (
          <>
            {range.label} window · {history.buckets.length} buckets · each
            bucket = {Math.round(history.period_seconds / 60)} min · ASG{' '}
            {history.asg_name}
            {loading && <span style={{ marginLeft: 8 }}>↻</span>}
          </>
        )}
      </div>
    </div>
  );
};

const TranscriptionFleetDashboard = ({ apiClient }) => {
  const [status, setStatus] = useState(null);
  const [statusError, setStatusError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [pollingActive, setPollingActive] = useState(true);

  const [desiredInput, setDesiredInput] = useState('');
  const [resizeRsp, setResizeRsp] = useState(null);
  const [resizing, setResizing] = useState(false);

  const [excludedWorkers, setExcludedWorkers] = useState(loadExcluded);

  const intervalRef = useRef(null);

  const toggleExclude = (instanceId) => {
    setExcludedWorkers((prev) => {
      const next = new Set(prev);
      next.has(instanceId) ? next.delete(instanceId) : next.add(instanceId);
      saveExcluded(next);
      return next;
    });
  };

  const clearExclusions = () => {
    setExcludedWorkers(new Set());
    sessionStorage.removeItem(EXCLUDED_WORKERS_KEY);
  };

  const fetchStatus = () => {
    apiClient
      .transcriptionStatus()
      .then((data) => {
        setStatus(data);
        setStatusError(null);
        setLastUpdated(new Date());
      })
      .catch((err) => setStatusError(String(err)));
  };

  useEffect(() => {
    fetchStatus();
    if (pollingActive) {
      // 10s cadence balances:
      //   - responsiveness: Resize fleet → 1 still appears inside one polling
      //     window, the eye can't tell 4s from 10s for "is the worker up yet"
      //   - cost: 4s was ~$1.50/day in CloudWatch + DDB read units when a
      //     dashboard tab was open all day; 10s cuts that ~2.5×
      //   - upstream cadence: ASG metrics publish at 1-min granularity and
      //     SQS attrs only update every few sec server-side anyway, so faster
      //     polling doesn't buy fresher data
      intervalRef.current = setInterval(fetchStatus, 10000);
    }
    return () => clearInterval(intervalRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pollingActive]);

  const handleResize = (desired) => {
    const val = desired != null ? desired : parseInt(desiredInput, 10);
    if (isNaN(val)) return;
    setResizing(true);
    setResizeRsp(null);
    apiClient
      .transcriptionResize(val)
      .then((data) => setResizeRsp(data))
      .catch((err) => setResizeRsp({ error: String(err) }))
      .finally(() => setResizing(false));
  };

  const fleet = status?.fleet ?? {};
  const queue = status?.queue ?? {};
  const throughput = status?.throughput ?? {};
  const traffic = status?.traffic;
  const workers = status?.workers ?? [];

  const dlq = queue.dlq_messages ?? 0;
  const visible = queue.messages_visible ?? 0;
  const bannerColor = queueColor(visible, dlq);

  // Headline-tier derived numbers
  const selfHostedPct = (traffic?.by_source?.['self-hosted']?.pct ?? 0) * 100;
  const selfHostedLatency = traffic?.by_source?.['self-hosted']?.avg_latency_s;
  const burnRate = workers
    .filter((w) => w.status === 'ready' || w.status === 'draining')
    .reduce((s, w) => s + (INSTANCE_HOURLY_COST[w.instance_type] ?? 0), 0);

  // Local aggregates — recomputed from included workers only
  const includedWorkers = workers.filter(
    (w) => !excludedWorkers.has(w.instance_id),
  );
  const isFiltered = excludedWorkers.size > 0;

  const localJobsDone = includedWorkers.reduce(
    (s, w) => s + (w.jobs_completed ?? 0),
    0,
  );
  const localJobsFailed = includedWorkers.reduce(
    (s, w) => s + (w.jobs_failed ?? 0),
    0,
  );
  const localErrorRate =
    localJobsDone + localJobsFailed > 0
      ? localJobsFailed / (localJobsDone + localJobsFailed)
      : 0;
  // Weighted-average RTF by jobs_completed
  const localRtf = (() => {
    const eligible = includedWorkers.filter(
      (w) => w.running_rtf != null && w.jobs_completed > 0,
    );
    if (!eligible.length) return null;
    const weightedSum = eligible.reduce(
      (s, w) => s + w.running_rtf * w.jobs_completed,
      0,
    );
    const totalJobs = eligible.reduce((s, w) => s + w.jobs_completed, 0);
    return totalJobs > 0 ? weightedSum / totalJobs : null;
  })();
  // Uptime-weighted utilization over included LIVE workers (ready/draining)
  // — mirrors the BE's throughput.fleet_utilization_pct, recomputed locally
  // when workers are excluded so the tile stays consistent with the filter.
  const localUtilizationPct = (() => {
    const live = includedWorkers.filter(
      (w) =>
        (w.status === 'ready' || w.status === 'draining') &&
        w.busy_fraction != null &&
        w.uptime_s > 0,
    );
    if (!live.length) return null;
    const busyWeighted = live.reduce(
      (s, w) => s + w.busy_fraction * w.uptime_s,
      0,
    );
    const uptimeSum = live.reduce((s, w) => s + w.uptime_s, 0);
    return uptimeSum > 0 ? (100 * busyWeighted) / uptimeSum : null;
  })();

  const tileStyle = {
    background: '#f8f9fa',
    border: '1px solid #dee2e6',
    borderRadius: 8,
    padding: '16px 20px',
    textAlign: 'center',
    minWidth: 120,
    flex: 1,
  };

  const tileLabel = {
    fontSize: '0.75rem',
    color: '#6c757d',
    marginBottom: 4,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
  };
  const tileValue = (color) => ({
    fontSize: '2rem',
    fontWeight: 700,
    color: color || '#343a40',
    lineHeight: 1,
  });

  const errorRate = isFiltered ? localErrorRate : throughput.error_rate ?? 0;
  const rtf = isFiltered ? localRtf : throughput.fleet_aggregate_rtf;
  const utilizationPct = isFiltered
    ? localUtilizationPct
    : throughput.fleet_utilization_pct;

  // Utilization color: the autoscaler targets ~70%. Low = paying for idle
  // capacity (the thing to notice); very high = risk of queueing.
  const utilizationColor = (pct) => {
    if (pct == null) return '#6c757d';
    if (pct < 40) return '#ffc107'; // over-provisioned — paying for idle
    if (pct > 90) return '#fd7e14'; // hot — under-provisioned / queueing risk
    return '#28a745'; // healthy band
  };

  // Tier 1 — headline tiles. Larger + more whitespace than tier 2.
  const headlineTileStyle = {
    background: '#fff',
    border: '1px solid #e3e6ea',
    borderRadius: 10,
    padding: '20px 22px',
    minWidth: 180,
    flex: 1,
    boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
  };
  const headlineLabel = {
    fontSize: '0.72rem',
    color: '#6c757d',
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
    marginBottom: 6,
  };
  const headlineValue = (color) => ({
    fontSize: '2.4rem',
    fontWeight: 700,
    color: color || '#212529',
    lineHeight: 1.05,
  });
  const headlineFootnote = {
    fontSize: '0.7rem',
    color: '#6c757d',
    marginTop: 8,
  };

  // Tier 2 — operational tiles. Smaller, single line of info.
  const smallTileStyle = {
    background: '#f8f9fa',
    border: '1px solid #dee2e6',
    borderRadius: 6,
    padding: '10px 14px',
    minWidth: 110,
    flex: 1,
  };
  const smallLabel = {
    fontSize: '0.7rem',
    color: '#6c757d',
    textTransform: 'uppercase',
  };
  const smallValue = (color) => ({
    fontSize: '1.3rem',
    fontWeight: 700,
    color: color || '#343a40',
    marginTop: 2,
  });

  return (
    <div style={{ fontFamily: 'inherit' }}>
      <h4 style={{ marginBottom: 16 }}>Transcription Fleet</h4>

      {/* ───── Tier 0 — alerts (only when present) ───── */}
      {dlq > 0 && (
        <div
          style={{
            background: '#dc3545',
            color: '#fff',
            padding: '10px 16px',
            borderRadius: 6,
            marginBottom: 12,
            fontWeight: 600,
          }}
        >
          ⚠ {dlq} message{dlq !== 1 ? 's' : ''} in the Dead-Letter Queue —
          workers crashed before deleting SQS messages. Investigate.
        </div>
      )}
      {fleet.workers_stale > 0 && (
        <div
          style={{
            background: '#fff3cd',
            color: '#856404',
            padding: '10px 16px',
            borderRadius: 6,
            marginBottom: 12,
            fontSize: '0.9rem',
          }}
        >
          {fleet.workers_stale} stale worker
          {fleet.workers_stale !== 1 ? 's' : ''} (heartbeat &gt; 90s). Likely
          killed ungracefully — see worker detail below.
        </div>
      )}
      {statusError && (
        <div
          style={{
            background: '#fff3cd',
            color: '#856404',
            padding: '10px 16px',
            borderRadius: 6,
            marginBottom: 12,
          }}
        >
          Error fetching status: {statusError}
        </div>
      )}

      {/* ───── Tier 1 — headline (the four numbers that matter) ───── */}
      {status && (
        <div
          style={{
            display: 'flex',
            gap: 14,
            marginBottom: 18,
            flexWrap: 'wrap',
          }}
        >
          {/* Self-hosted share + traffic split */}
          <div style={headlineTileStyle}>
            <div style={headlineLabel}>Self-hosted share</div>
            <div style={headlineValue('#28a745')}>
              {traffic?.total > 0 ? `${Math.round(selfHostedPct)}%` : '—'}
            </div>
            <TrafficSplitBar traffic={traffic} />
            <div style={headlineFootnote}>
              {traffic?.total ?? 0} transcriptions / last{' '}
              {traffic?.window_minutes ?? 5}m
            </div>
          </div>

          {/* Average user-perceived latency by source */}
          <div style={headlineTileStyle}>
            <div style={headlineLabel}>User latency</div>
            <div style={headlineValue()}>
              {selfHostedLatency != null
                ? `${selfHostedLatency.toFixed(1)}s`
                : traffic?.total > 0
                  ? '—'
                  : '—'}
            </div>
            <LatencyByProviderBars traffic={traffic} />
            <div style={headlineFootnote}>
              from dynamo entry → transcript returned, per provider
            </div>
          </div>

          {/* $/hr current burn */}
          <div style={headlineTileStyle}>
            <div style={headlineLabel}>$ / hr burn</div>
            <div style={headlineValue(burnRate > 0 ? '#fd7e14' : '#212529')}>
              ${burnRate.toFixed(2)}
            </div>
            <div style={headlineFootnote}>
              {fleet.workers_ready ?? 0} ready · {fleet.workers_draining ?? 0}{' '}
              draining
              {fleet.asg_desired_capacity != null &&
                ` · target ${fleet.asg_desired_capacity}`}
            </div>
          </div>

          {/* Queue activity (subordinate to the above three but live signal) */}
          <div style={headlineTileStyle}>
            <div style={headlineLabel}>Queue depth</div>
            <div style={headlineValue(bannerColor)}>{visible}</div>
            <div style={headlineFootnote}>
              {queue.messages_in_flight ?? 0} in-flight
              {dlq > 0 && (
                <span style={{ color: '#dc3545', fontWeight: 600 }}>
                  {' '}
                  · DLQ {dlq}
                </span>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ───── Tier 2 — operational (worker-side metrics) ───── */}
      {status && (
        <div
          style={{
            display: 'flex',
            gap: 8,
            marginBottom: 18,
            flexWrap: 'wrap',
          }}
        >
          <div style={smallTileStyle}>
            <div style={smallLabel}>Fleet RTF{isFiltered ? ' *' : ''}</div>
            <div
              style={smallValue(
                rtf != null && rtf < 30 ? '#ffc107' : '#28a745',
              )}
            >
              {fmt(rtf)}x
            </div>
          </div>
          <div
            style={smallTileStyle}
            title="Efficiency: % of live-worker uptime spent actually transcribing (GPU-busy). RTF is speed; this is how much of what you're paying for is useful work. Autoscaler targets ~70%; low = over-provisioned / paying for idle."
          >
            <div style={smallLabel}>Utilization{isFiltered ? ' *' : ''}</div>
            <div style={smallValue(utilizationColor(utilizationPct))}>
              {utilizationPct != null ? `${utilizationPct.toFixed(0)}%` : '—'}
            </div>
          </div>
          <div style={smallTileStyle}>
            <div style={smallLabel}>Error rate{isFiltered ? ' *' : ''}</div>
            <div style={smallValue(errorRate > 0.01 ? '#ffc107' : '#343a40')}>
              {(errorRate * 100).toFixed(2)}%
            </div>
          </div>
          <div style={smallTileStyle}>
            <div style={smallLabel}>Avg clip</div>
            <div style={smallValue()}>
              {fmt(throughput.avg_clip_audio_s, 0)}s
            </div>
          </div>
          <div style={smallTileStyle}>
            <div style={smallLabel}>Jobs done{isFiltered ? ' *' : ''}</div>
            <div style={smallValue()}>
              {isFiltered
                ? localJobsDone
                : throughput.jobs_completed_total ?? '—'}
            </div>
          </div>
          <div style={smallTileStyle}>
            <div style={smallLabel}>Jobs failed{isFiltered ? ' *' : ''}</div>
            <div
              style={smallValue(
                (isFiltered ? localJobsFailed : throughput.jobs_failed_total) >
                  0
                  ? '#dc3545'
                  : '#343a40',
              )}
            >
              {isFiltered
                ? localJobsFailed
                : throughput.jobs_failed_total ?? '—'}
            </div>
          </div>
        </div>
      )}

      {/* ───── Tier 2.5 — historical view (24h default) ───── */}
      {/* Lives between the live-snapshot tiles and the resize controls so the
          op can see "we had 3 workers an hour ago, that's why the queue is
          empty now" without scrolling past the action buttons. */}
      <WorkerHistoryChart apiClient={apiClient} />

      {/* ───── Tier 3 — resize controls + polling toggle ───── */}
      <div
        style={{
          background: '#f8f9fa',
          border: '1px solid #dee2e6',
          borderRadius: 6,
          padding: '10px 14px',
          marginBottom: 16,
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            flexWrap: 'wrap',
          }}
        >
          <strong style={{ fontSize: '0.85rem' }}>Resize fleet</strong>
          <div style={{ display: 'flex', gap: 6 }}>
            <Button
              color="danger"
              size="sm"
              disabled={resizing}
              onClick={() => handleResize(0)}
            >
              0
            </Button>
            <Button
              color="warning"
              size="sm"
              disabled={resizing}
              onClick={() => handleResize(1)}
            >
              1
            </Button>
            <Button
              color="info"
              size="sm"
              disabled={resizing}
              onClick={() => handleResize(3)}
            >
              3
            </Button>
            <Button
              color="secondary"
              size="sm"
              disabled={resizing}
              onClick={() => handleResize(5)}
            >
              5
            </Button>
          </div>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <Input
              type="number"
              min={0}
              max={fleet.asg_max_size ?? 150}
              placeholder="custom"
              value={desiredInput}
              onChange={(e) => setDesiredInput(e.target.value)}
              style={{ width: 80 }}
              bsSize="sm"
            />
            <Button
              color="primary"
              size="sm"
              disabled={resizing || desiredInput === ''}
              onClick={() => handleResize()}
            >
              {resizing ? '…' : 'Set'}
            </Button>
          </div>
          <div
            style={{
              marginLeft: 'auto',
              display: 'flex',
              gap: 6,
              alignItems: 'center',
            }}
          >
            <Button
              color={pollingActive ? 'success' : 'secondary'}
              size="sm"
              onClick={() => setPollingActive((p) => !p)}
            >
              {pollingActive ? '⏸' : '▶'}
            </Button>
            <Button color="light" size="sm" onClick={fetchStatus}>
              ↻
            </Button>
            {lastUpdated && (
              <span style={{ fontSize: '0.72rem', color: '#6c757d' }}>
                {lastUpdated.toLocaleTimeString()}
              </span>
            )}
          </div>
        </div>
        {resizeRsp && (
          <div
            style={{
              marginTop: 8,
              fontSize: '0.8rem',
              background: resizeRsp.error ? '#f8d7da' : '#d1e7dd',
              padding: '6px 10px',
              borderRadius: 4,
            }}
          >
            {resizeRsp.error
              ? `Error: ${resizeRsp.error}`
              : `✓ ${resizeRsp.previous_desired} → ${resizeRsp.new_desired}`}
          </div>
        )}
      </div>

      {/* ───── Tier 4 — collapsible worker detail ───── */}
      <details
        style={{
          background: '#fff',
          border: '1px solid #e3e6ea',
          borderRadius: 6,
          padding: 0,
        }}
      >
        <summary
          style={{
            cursor: 'pointer',
            padding: '10px 14px',
            fontWeight: 600,
            fontSize: '0.85rem',
            userSelect: 'none',
          }}
        >
          Worker detail ({workers.length})
          {fleet.workers_lost > 0 && (
            <span style={{ color: '#6f42c1', fontWeight: 400, marginLeft: 8 }}>
              · {fleet.workers_lost} lost
            </span>
          )}
          {fleet.workers_terminated > 0 && (
            <span style={{ color: '#6c757d', fontWeight: 400, marginLeft: 8 }}>
              · {fleet.workers_terminated} terminated (24h TTL)
            </span>
          )}
          {fleet.asg_desired_capacity != null &&
            ` · ASG ${fleet.asg_desired_capacity} desired (min ${fleet.asg_min_size}, max ${fleet.asg_max_size})`}
          {isFiltered && (
            <span style={{ color: '#6c757d', fontWeight: 400, marginLeft: 8 }}>
              · {excludedWorkers.size} excluded
            </span>
          )}
        </summary>
        <div style={{ padding: '0 14px 14px' }}>
          {isFiltered && (
            <div style={{ marginBottom: 8 }}>
              <Button
                color="link"
                size="sm"
                style={{ padding: 0, fontSize: '0.78rem' }}
                onClick={clearExclusions}
              >
                Clear exclusions
              </Button>
            </div>
          )}
          {workers.length === 0 && (
            <div
              style={{
                color: '#6c757d',
                padding: '10px 0',
                fontSize: '0.85rem',
              }}
            >
              No workers.
            </div>
          )}
          {workers.length > 0 && (
            <div style={{ overflowX: 'auto' }}>
              <Table
                size="sm"
                bordered
                hover
                responsive
                style={{ fontSize: '0.82rem', marginBottom: 0 }}
              >
                <thead style={{ background: '#e9ecef' }}>
                  <tr>
                    <th>Instance ID</th>
                    <th>Type</th>
                    <th>Status</th>
                    <th>Uptime</th>
                    <th>Heartbeat</th>
                    <th>Jobs</th>
                    <th>Failed</th>
                    <th>RTF</th>
                    <th title="Lifetime utilization: share of uptime spent transcribing.">
                      Util
                    </th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {workers.map((w) => {
                    const isTerminated = w.status === 'terminated';
                    const isLost = w.presumed_lost === true;
                    // STALE *only* when the BE didn't already reclassify
                    // to lost — otherwise the heartbeat-age check would
                    // double-fire on the same row.
                    const isStale =
                      !isTerminated &&
                      !isLost &&
                      w.last_heartbeat_age_s != null &&
                      w.last_heartbeat_age_s > 90;
                    const isDead = isTerminated || isLost;
                    const isExcluded = excludedWorkers.has(w.instance_id);
                    // Heartbeat-age column shows "how long ago it died" for
                    // dead rows, "how long since the last heartbeat" for
                    // live/stale rows.
                    const deathAge =
                      isDead && w.terminated_at_age_s != null
                        ? w.terminated_at_age_s
                        : w.last_heartbeat_age_s;
                    return (
                      <tr
                        key={w.instance_id}
                        style={{
                          ...(isStale ? { background: '#fff5f5' } : {}),
                          ...(isDead
                            ? { background: '#f8f9fa', color: '#6c757d' }
                            : {}),
                          ...(isExcluded ? { opacity: 0.45 } : {}),
                        }}
                      >
                        <td
                          style={{
                            fontFamily: 'monospace',
                            fontSize: '0.78rem',
                          }}
                          title={
                            w.asg_lifecycle_state
                              ? `ASG: ${w.asg_lifecycle_state}`
                              : 'ASG state unknown (worker stack not deployed, or local-run worker).'
                          }
                        >
                          {w.instance_id}
                        </td>
                        <td>{w.instance_type}</td>
                        <td>{workerStatusBadge(w)}</td>
                        <td>{fmtUptime(w.uptime_s)}</td>
                        <td
                          style={{
                            color: isDead
                              ? '#6c757d'
                              : isStale
                                ? '#dc3545'
                                : w.last_heartbeat_age_s > 30
                                  ? '#fd7e14'
                                  : 'inherit',
                          }}
                          title={
                            isLost
                              ? 'Heartbeat went stale; the ASG no longer has this instance. Worker died without cleanup.'
                              : isTerminated
                                ? 'Time since this worker was terminated. DDB prunes the row 24h after shutdown.'
                                : 'Seconds since the last heartbeat.'
                          }
                        >
                          {deathAge != null
                            ? isDead
                              ? `died ${fmtUptime(deathAge)} ago`
                              : `${deathAge}s`
                            : '—'}
                        </td>
                        <td>{w.jobs_completed}</td>
                        <td
                          style={{
                            color:
                              !isDead && w.jobs_failed > 0
                                ? '#dc3545'
                                : 'inherit',
                          }}
                        >
                          {w.jobs_failed}
                        </td>
                        <td>{fmt(w.running_rtf)}x</td>
                        <td
                          style={{
                            color:
                              !isDead &&
                              w.busy_fraction != null &&
                              w.busy_fraction < 0.4
                                ? '#b8860b'
                                : 'inherit',
                          }}
                        >
                          {w.busy_fraction != null
                            ? `${Math.round(w.busy_fraction * 100)}%`
                            : '—'}
                        </td>
                        <td>
                          <Button
                            size="sm"
                            color={isExcluded ? 'secondary' : 'warning'}
                            outline={!isExcluded}
                            style={{
                              fontSize: '0.7rem',
                              padding: '1px 7px',
                              whiteSpace: 'nowrap',
                            }}
                            onClick={() => toggleExclude(w.instance_id)}
                          >
                            {isExcluded ? '+ Include' : '− Exclude'}
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </div>
          )}
        </div>
      </details>

      {!status && !statusError && (
        <div
          style={{ color: '#6c757d', padding: '20px 0', textAlign: 'center' }}
        >
          Loading fleet status…
        </div>
      )}
    </div>
  );
};

// ─────────────────────── Demo Playground funnel ─────────────────────────────
// Visualizes the EMR Demo Playground's DEMO_FUNNEL beacons (landing → EMR
// pick → tour → comprehend → lead → account) via iTools /demo/funnel, which
// reads the main API's logs. Shows where prospects fall off and who left
// their email.
const DEMO_FUNNEL_RANGES = [
  { label: '24h', hours: 24 },
  { label: '7d', hours: 168 },
  { label: '30d', hours: 720 },
];

const DEMO_STAGE_LABELS = {
  landing_view: 'Landing viewed',
  emr_selected: 'EMR selected',
  playground_loaded: 'Playground loaded',
  sandbox_minted: 'Sandbox minted',
  panel_open: 'Panel opened',
  tour_started: 'Tour started',
  visit_created: 'Visit created',
  transcript_ready: 'Transcript ready',
  comprehended: 'Comprehended',
  tour_completed: 'Tour completed',
  chat_answered: 'Chat answered',
  lead_captured: 'Lead captured',
  account_created: 'Account created',
  teammates_invited: 'Teammates invited',
  extension_clicked: 'Extension clicked',
};

const DemoPlaygroundFunnel = ({ apiClient }) => {
  const [rangeIdx, setRangeIdx] = useState(1); // default 7d
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [modeFilter, setModeFilter] = useState('all');
  const [personQuery, setPersonQuery] = useState('');
  const [expandedPerson, setExpandedPerson] = useState(null);

  const range = DEMO_FUNNEL_RANGES[rangeIdx];

  const refetch = () => {
    setLoading(true);
    apiClient
      .demoFunnel(range.hours)
      .then((rsp) => {
        if (rsp && rsp.error) {
          setError(String(rsp.error));
        } else {
          setData(rsp);
          setError(null);
        }
      })
      .catch((err) => setError(String(err)))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rangeIdx]);

  const stages = data?.stages ?? Object.keys(DEMO_STAGE_LABELS);
  const counts = data?.counts ?? {};
  const byEmr = data?.by_emr ?? {};
  const leads = data?.leads ?? [];
  const recent = data?.recent ?? [];
  const people = data?.people ?? [];
  const byMode = data?.by_mode ?? {};
  // Who did what: filter by playground mode (sales demo / prospect preview /
  // signed-in onboarding) plus a free-text search; a row opens its timeline.
  const personQ = personQuery.trim().toLowerCase();
  const peopleShown = people.filter((p) => {
    if (modeFilter !== 'all' && !(p.modes && p.modes.length ? p.modes : ['demo']).includes(modeFilter)) return false;
    if (!personQ) return true;
    return [p.who, p.email, p.name, ...(p.emrs || [])]
      .filter(Boolean)
      .some((v) => String(v).toLowerCase().includes(personQ));
  });
  const maxCount = Math.max(1, ...stages.map((st) => counts[st] ?? 0));
  const topOfFunnel = counts[stages[0]] ?? 0;

  // Auxiliary events (tour_step / tour_back / ...) aren't funnel stages but
  // still worth a glance.
  const auxEvents = Object.keys(counts).filter((e) => !stages.includes(e));

  const th = {
    fontSize: '0.72rem',
    textTransform: 'uppercase',
    color: '#8898aa',
    textAlign: 'left',
    padding: '6px 10px',
    borderBottom: '1px solid #e9ecef',
  };
  const td = {
    fontSize: '0.8rem',
    padding: '6px 10px',
    borderBottom: '1px solid #f1f3f5',
    verticalAlign: 'top',
  };

  return (
    <div>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          flexWrap: 'wrap',
          marginBottom: 14,
        }}
      >
        <strong>Demo Playground funnel</strong>
        <div style={{ display: 'flex', gap: 4 }}>
          {DEMO_FUNNEL_RANGES.map((r, i) => (
            <Button
              key={r.label}
              size="sm"
              color={i === rangeIdx ? 'primary' : 'secondary'}
              onClick={() => setRangeIdx(i)}
            >
              {r.label}
            </Button>
          ))}
        </div>
        <Button size="sm" color="info" onClick={refetch} disabled={loading}>
          {loading ? 'Loading…' : 'Refresh'}
        </Button>
        {data?.as_of && (
          <span style={{ fontSize: '0.75rem', color: '#8898aa' }}>
            as of {data.as_of} · {data.log_group}
            {data.truncated ? ' · ⚠ truncated at query limit' : ''}
          </span>
        )}
      </div>

      {error && (
        <div
          style={{
            background: '#fff5f5',
            border: '1px solid #ffc9c9',
            color: '#c92a2a',
            borderRadius: 6,
            padding: '8px 12px',
            marginBottom: 12,
            fontSize: '0.85rem',
          }}
        >
          {error}
        </div>
      )}

      {/* ───── Funnel bars ───── */}
      <div style={{ marginBottom: 22 }}>
        {stages.map((st) => {
          const n = counts[st] ?? 0;
          const pctOfTop = topOfFunnel > 0 ? (n / topOfFunnel) * 100 : 0;
          return (
            <div
              key={st}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                marginBottom: 4,
              }}
            >
              <span
                style={{
                  width: 150,
                  fontSize: '0.78rem',
                  color: '#495057',
                  textAlign: 'right',
                  flex: 'none',
                }}
              >
                {DEMO_STAGE_LABELS[st] || st}
              </span>
              <div
                style={{
                  flex: 1,
                  background: '#f1f3f5',
                  borderRadius: 4,
                  height: 20,
                  position: 'relative',
                }}
              >
                <div
                  style={{
                    width: `${(n / maxCount) * 100}%`,
                    minWidth: n > 0 ? 3 : 0,
                    height: '100%',
                    borderRadius: 4,
                    background:
                      st === 'account_created' || st === 'lead_captured'
                        ? '#2dce89'
                        : '#5e72e4',
                    transition: 'width .25s ease',
                  }}
                />
              </div>
              <span
                style={{
                  width: 90,
                  fontSize: '0.78rem',
                  color: '#343a40',
                  flex: 'none',
                }}
              >
                {n}
                {topOfFunnel > 0 && (
                  <span style={{ color: '#8898aa' }}>
                    {' '}
                    · {pctOfTop.toFixed(0)}%
                  </span>
                )}
              </span>
            </div>
          );
        })}
        {auxEvents.length > 0 && (
          <div style={{ fontSize: '0.75rem', color: '#8898aa', marginTop: 8 }}>
            Also:{' '}
            {auxEvents.map((e) => `${e}=${counts[e]}`).join(', ')}
          </div>
        )}
      </div>

      {/* ───── Per-EMR split ───── */}
      {Object.keys(byEmr).length > 0 && (
        <div style={{ marginBottom: 22, overflowX: 'auto' }}>
          <strong style={{ fontSize: '0.85rem' }}>By EMR</strong>
          <table style={{ borderCollapse: 'collapse', marginTop: 6 }}>
            <thead>
              <tr>
                <th style={th}>EMR</th>
                <th style={th}>Selected</th>
                <th style={th}>Loaded</th>
                <th style={th}>Comprehended</th>
                <th style={th}>Tour done</th>
                <th style={th}>Leads</th>
                <th style={th}>Accounts</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(byEmr).map(([emr, c]) => (
                <tr key={emr}>
                  <td style={{ ...td, fontWeight: 600 }}>{emr}</td>
                  <td style={td}>{c.emr_selected ?? 0}</td>
                  <td style={td}>{c.playground_loaded ?? 0}</td>
                  <td style={td}>{c.comprehended ?? 0}</td>
                  <td style={td}>{c.tour_completed ?? 0}</td>
                  <td style={td}>{c.lead_captured ?? 0}</td>
                  <td style={td}>{c.account_created ?? 0}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ───── Leads ───── */}
      <div style={{ marginBottom: 22, overflowX: 'auto' }}>
        <strong style={{ fontSize: '0.85rem' }}>
          Leads ({leads.length})
        </strong>
        {leads.length === 0 ? (
          <div style={{ fontSize: '0.8rem', color: '#8898aa', marginTop: 4 }}>
            No leads captured in this window.
          </div>
        ) : (
          <table style={{ borderCollapse: 'collapse', marginTop: 6 }}>
            <thead>
              <tr>
                <th style={th}>When (UTC)</th>
                <th style={th}>Event</th>
                <th style={th}>Email</th>
                <th style={th}>Name</th>
                <th style={th}>Providers</th>
                <th style={th}>Teammates</th>
                <th style={th}>EMR</th>
              </tr>
            </thead>
            <tbody>
              {leads.map((l, i) => (
                <tr key={i}>
                  <td style={td}>{l.ts}</td>
                  <td style={td}>{l.event}</td>
                  <td style={{ ...td, fontWeight: 600 }}>{l.email || '—'}</td>
                  <td style={td}>{l.name || '—'}</td>
                  <td style={td}>{l.providers || '—'}</td>
                  <td style={td}>{l.teammates || '—'}</td>
                  <td style={td}>{l.emr || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* ───── Who did what ───── */}
      <div style={{ marginBottom: 22 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 6 }}>
          <strong style={{ fontSize: '0.85rem' }}>
            People ({peopleShown.length}
            {peopleShown.length !== people.length ? ` of ${people.length}` : ''})
          </strong>
          <div style={{ display: 'flex', gap: 4 }}>
            {['all', 'onboard', 'preview', 'demo'].map((m) => (
              <Button
                key={m}
                size="sm"
                color={m === modeFilter ? 'primary' : 'secondary'}
                onClick={() => setModeFilter(m)}
              >
                {m === 'all' ? 'All' : m}
                {m !== 'all' && byMode[m]
                  ? ` · ${Object.values(byMode[m]).reduce((a, b) => a + b, 0)}`
                  : ''}
              </Button>
            ))}
          </div>
          <input
            type="search"
            placeholder="email, name, EMR, IP…"
            value={personQuery}
            onChange={(e) => setPersonQuery(e.target.value)}
            style={{ fontSize: '0.8rem', padding: '4px 8px', border: '1px solid #dee2e6', borderRadius: 4, minWidth: 220 }}
          />
          <span style={{ fontSize: '0.72rem', color: '#8898aa' }}>
            Anonymous visitors are grouped by IP; an IP that later left an email is folded into that person. Click a row for the timeline.
          </span>
        </div>
        {peopleShown.length === 0 ? (
          <div style={{ fontSize: '0.8rem', color: '#8898aa' }}>Nobody in this window.</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ borderCollapse: 'collapse', width: '100%' }}>
              <thead>
                <tr>
                  <th style={th}>Who</th>
                  <th style={th}>EMR</th>
                  <th style={th}>Mode</th>
                  <th style={th}>Last seen (UTC)</th>
                  <th style={th}>First seen</th>
                  <th style={th}>Events</th>
                  <th style={th}>Furthest</th>
                  <th style={th}>Milestones</th>
                  <th style={th}>Last step</th>
                </tr>
              </thead>
              <tbody>
                {peopleShown.map((p) => {
                  const open = expandedPerson === p.who;
                  const flags = p.flags || {};
                  const milestones = [
                    ['comprehended', 'Comprehended'],
                    ['lead_captured', 'Lead'],
                    ['account_created', 'Account'],
                    ['extension_clicked', 'Extension'],
                    ['tour_completed', 'Tour done'],
                    ['chat_answered', 'Chat'],
                  ].filter(([k]) => flags[k]);
                  return (
                    <Fragment key={p.who}>
                      <tr
                        onClick={() => setExpandedPerson(open ? null : p.who)}
                        style={{ cursor: 'pointer', background: open ? '#f8f9fe' : undefined }}
                      >
                        <td style={{ ...td, fontWeight: 600, whiteSpace: 'nowrap' }}>
                          {p.email ? (
                            <>
                              {p.email}
                              {p.name ? <div style={{ fontWeight: 400, color: '#8898aa' }}>{p.name}</div> : null}
                            </>
                          ) : (
                            <span style={{ color: '#8898aa' }}>anonymous · {p.who.replace(/^ip:/, '')}</span>
                          )}
                        </td>
                        <td style={td}>{(p.emrs || []).join(', ') || '—'}</td>
                        <td style={td}>{(p.modes || []).join(', ') || 'demo'}</td>
                        <td style={{ ...td, whiteSpace: 'nowrap' }}>{p.last_seen || '—'}</td>
                        <td style={{ ...td, whiteSpace: 'nowrap' }}>{p.first_seen || '—'}</td>
                        <td style={td}>{p.events}</td>
                        <td style={td}>{DEMO_STAGE_LABELS[p.furthest] || p.furthest || '—'}</td>
                        <td style={td}>
                          {milestones.length === 0
                            ? '—'
                            : milestones.map(([k, label]) => (
                                <span
                                  key={k}
                                  style={{
                                    display: 'inline-block',
                                    fontSize: '0.7rem',
                                    padding: '1px 6px',
                                    marginRight: 4,
                                    borderRadius: 10,
                                    background: k === 'account_created' || k === 'lead_captured' ? '#d3f9d8' : '#e7ecff',
                                    color: '#343a40',
                                  }}
                                >
                                  {label}
                                </span>
                              ))}
                        </td>
                        <td style={{ ...td, color: '#8898aa' }}>{p.last_step || '—'}</td>
                      </tr>
                      {open && (
                        <tr>
                          <td colSpan={9} style={{ ...td, background: '#f8f9fe', padding: '4px 10px 10px 24px' }}>
                            <div style={{ fontSize: '0.72rem', color: '#8898aa', marginBottom: 4 }}>Timeline, newest first</div>
                            {(p.timeline || []).map((t, i) => (
                              <div key={i} style={{ fontSize: '0.78rem', display: 'flex', gap: 12 }}>
                                <span style={{ color: '#8898aa', whiteSpace: 'nowrap', width: 170 }}>{t.ts}</span>
                                <span style={{ fontWeight: 600, width: 150 }}>{DEMO_STAGE_LABELS[t.event] || t.event}</span>
                                <span style={{ color: '#495057' }}>{[t.step, t.emr, t.mode].filter(Boolean).join(' · ')}</span>
                              </div>
                            ))}
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {/* ───── Recent events ───── */}
      <details>
        <summary
          style={{ fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer' }}
        >
          Recent events ({recent.length})
        </summary>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ borderCollapse: 'collapse', marginTop: 6 }}>
            <thead>
              <tr>
                <th style={th}>When (UTC)</th>
                <th style={th}>Event</th>
                <th style={th}>EMR</th>
                <th style={th}>Scenario</th>
                <th style={th}>Step</th>
                <th style={th}>IP</th>
              </tr>
            </thead>
            <tbody>
              {recent.map((r, i) => (
                <tr key={i}>
                  <td style={td}>{r.ts}</td>
                  <td style={td}>{r.event}</td>
                  <td style={td}>{r.emr || '—'}</td>
                  <td style={td}>{r.scenario || '—'}</td>
                  <td style={td}>{r.step || '—'}</td>
                  <td style={td}>{r.ip || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </details>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────

const AdminDashboard = () => {
  const { apiClient } = useContext(GlobalContext);
  const navigate = useNavigate();

  const [mode, setMode] = useState('DevMasterDashboard');

  // Full screens behind their own access gate, so they are navigated to rather
  // than rendered inside the dashboard.
  const STANDALONE_TOOLS = {
    SessionReplays: '/User/admin/replays',
    MultiRecordAttribution: '/User/admin/multirecord',
  };

  const changeMode = (nextMode) => {
    if (STANDALONE_TOOLS[nextMode]) {
      navigate(STANDALONE_TOOLS[nextMode]);
      return;
    }
    setMode(nextMode);
  };

  return (
    <>
      <Header isLite={true} />
      <Container className="mt--7" fluid>
        <Row>
          <div className="col">
            <Card className="shadow">
              <CardHeader
                style={{
                  fontSize: '1.2rem',
                  textAlign: 'center',
                }}
              >
                <div>
                  <Input
                    type="select"
                    value={mode}
                    onChange={(e) => changeMode(e.target.value)}
                    style={{
                      maxWidth: '300px',
                      margin: '1rem auto',
                      fontSize: '1.1rem',
                      padding: '0.5rem',
                    }}
                  >
                    <option value="DevMasterDashboard">
                      Dev Master Dashboard
                    </option>
                    <option value="NoteQualityDashboard">Note Editing</option>
                    <option value="FeedbackDashboard">Note Feedback</option>
                    <option value="SearchUser">Search User</option>
                    <option value="SetExpirationAndPromo">
                      Set Expiration and Promo
                    </option>
                    <option value="GetConfigs">Get Configs</option>
                    <option value="GetUserDetails">Get User Details</option>
                    <option value="GetUsage">GetUsage</option>
                    <option value="ChangeEmail">Change Email</option>
                    <option value="GroupSubscription">
                      Group Subscription
                    </option>
                    <option value="PartnerSecrets">Partner Secrets</option>
                    <option value="FeatureManagement">
                      Feature Management
                    </option>
                    <option value="FeatureRollouts">Feature Rollouts</option>
                    <option value="InstructionPush">Instruction Push</option>
                    <option value="InstructionAssistantPrompts">
                      Instruction Assistant Prompts
                    </option>
                    <option value="ModifyPeople">Modify People</option>
                    <option value="ChartMogulCRUD">ChartMogul CRUD</option>
                    <option value="MockEMRDevTools">Mock EMR Dev Tools</option>
                    <option value="TranscriptionFleet">
                      Transcription Fleet
                    </option>
                    <option value="EMRInjector">EMR Injector</option>
                    <option value="EMRDirectives">EMR Directives</option>
                    <option value="DemoPlaygroundFunnel">
                      Demo Playground Funnel
                    </option>
                    <option value="SessionReplays">Session Replays</option>
                    <option value="MultiRecordAttribution">
                      Multi-record Attribution
                    </option>
                    <option value="LearnEdits">Learn++ Analytics</option>
                  </Input>
                </div>
              </CardHeader>

              <div style={{ padding: '20px' }}>
                {mode === 'DevMasterDashboard' && (
                  <DevMasterDashboard apiClient={apiClient} />
                )}
                {mode === 'NoteQualityDashboard' && (
                  <NoteQualityDashboard apiClient={apiClient} />
                )}
                {mode === 'FeedbackDashboard' && (
                  <FeedbackDashboard apiClient={apiClient} />
                )}
                {mode === 'SearchUser' && <SearchUser apiClient={apiClient} />}
                {mode === 'GetConfigs' && <GetConfigs apiClient={apiClient} />}
                {mode === 'GetUserDetails' && (
                  <GetUserDetails apiClient={apiClient} />
                )}
                {mode === 'GetUsage' && <GetUsage apiClient={apiClient} />}

                {mode === 'ChangeEmail' && (
                  <AdminChangeEmail apiClient={apiClient} />
                )}
                {mode === 'SetExpirationAndPromo' && (
                  <SetExpirationAndPromo apiClient={apiClient} />
                )}
                {mode === 'GroupSubscription' && (
                  <GroupSubscription apiClient={apiClient} />
                )}
                {mode === 'PartnerSecrets' && (
                  <PartnerCredentials apiClient={apiClient} />
                )}
                {mode === 'FeatureManagement' && (
                  <FeatureManagement apiClient={apiClient} />
                )}
                {mode === 'FeatureRollouts' && (
                  <FeatureRollouts apiClient={apiClient} />
                )}
                {mode === 'InstructionPush' && (
                  <InstructionPush apiClient={apiClient} />
                )}
                {mode === 'InstructionAssistantPrompts' && (
                  <InstructionAssistantPrompts apiClient={apiClient} />
                )}
                {mode === 'ModifyPeople' && (
                  <ModifyPeople apiClient={apiClient} />
                )}
                {mode === 'ChartMogulCRUD' && (
                  <ChartMogulCRUD apiClient={apiClient} />
                )}
                {mode === 'MockEMRDevTools' && <MockEMRDevTools />}
                {mode === 'TranscriptionFleet' && (
                  <TranscriptionFleetDashboard apiClient={apiClient} />
                )}
                {mode === 'EMRInjector' && (
                  <EMRInjectorDebug apiClient={apiClient} />
                )}
                {mode === 'EMRDirectives' && (
                  <EMRDirectives apiClient={apiClient} />
                )}
                {mode === 'DemoPlaygroundFunnel' && (
                  <DemoPlaygroundFunnel apiClient={apiClient} />
                )}
                {mode === 'LearnEdits' && (
                  <LearnEditsPanel apiClient={apiClient} />
                )}
              </div>
            </Card>
          </div>
        </Row>
      </Container>
    </>
  );
};

export default AdminDashboard;
